// A face, drawn (PLAN-2026-10-24-the-face-maker §4, B-488; the bots' faces,
// AI_MANAGERS.md §5.4, B-464): its parts from `@fsp/domain`'s `Face`, as one
// SVG on a 100 × 100 box with the head where the bots' always was.
//
// EVERY HEAD SHARES ONE CROWN, the top half of a circle around (50, 56), and
// differs below the eyes, so hair, hats, ears and glasses fit every head and
// only what hangs from the chin moves with its length (`CHIN`).
//
// A HAT HIDES THE HAIR IT SITS ON: each says how far down it covers, and the
// hair is clipped there, so an afro under a cap bulges at the sides.
//
// EVERY PAINTABLE REGION NAMES ITS AREA (`data-slot`), which is how the paint
// can knows what it was tapped on; a detail with no area of its own lets a tap
// through to what is under it.
//
// THE OUTFIT SHOWS ON THE BADGE once it is chosen (PLAN-2026-10-24-faces-
// round-three §2.1): its shoulders at the bottom of the disc, as a garment
// worn at the neck always did. The character itself is `FigureArt`, which
// draws this same head (`FaceDrawing`) on a body.
//
// THE STATE IS A CLASS NAME (§5.1): idle, thinking, pleased, annoyed, talking,
// and the stickers' surprised, asleep and waving (PLAN-2026-10-30-stickers).
// Every mouth is drawn and the state shows one, so a mood changes nothing but
// CSS, and nothing moves under reduce motion (kit.css).

import { useId, type ReactNode, type SVGProps } from 'react';
import {
  GARMENT_NECKS,
  NO_ADJUST,
  NO_TEAM,
  darken,
  faceOutfit,
  figureColors,
  hatColors,
  type Face,
  type FaceArea,
  type FaceMood,
  type FaceOutfit,
  type FigureTeam,
  type WeeklyHat,
} from '@fsp/domain';

/** The face's ink: outlines, pupils, a mouth's line, the same on every skin. */
export const INK = '#2b1d16';
const MOUTH = '#5a1a1a';
const TONGUE = '#ef6f8a';
const METAL = '#9aa5b5';

/** How far below the round head's chin (87) each head's chin sits. */
const CHIN: Record<Face['head'], number> = {
  round: 0,
  oval: 3,
  square: 2,
  long: 6,
  heart: 4,
  wide: 1,
  robot: -1,
};

/** The crown every head shares, from the left of the eye line over the top to the right. */
const CROWN = 'M19 56 A31 31 0 0 1 81 56';
const HEADS: Record<Face['head'], string> = {
  round: `${CROWN} A31 31 0 0 1 19 56 Z`,
  oval: `${CROWN} C81 76 68 90 50 90 C32 90 19 76 19 56 Z`,
  square: `${CROWN} L81 72 Q81 88 64 89 L36 89 Q19 88 19 72 Z`,
  long: `${CROWN} C80 82 64 93 50 93 C36 93 20 82 19 56 Z`,
  heart: `${CROWN} C81 72 62 88 50 91 C38 88 19 72 19 56 Z`,
  wide: `${CROWN} C85 70 76 88 50 88 C24 88 15 70 19 56 Z`,
  robot: 'M19 40 Q19 26 33 26 L67 26 Q81 26 81 40 L81 74 Q81 86 67 86 L33 86 Q19 86 19 74 Z',
};

/** A path mirrored across the face's middle (x → 100 − x). Absolute M, L, Q, C and Z only. */
export function mx(d: string): string {
  let i = 0;
  return d.replace(/-?\d*\.?\d+/g, (n) => (i++ % 2 === 0 ? String(100 - Number(n)) : n));
}

/** Both sides of a symmetric part: the left as drawn, the right mirrored. */
export function Both({ d, ...rest }: { d: string } & SVGProps<SVGPathElement>) {
  return (
    <>
      <path d={d} {...rest} />
      <path d={mx(d)} {...rest} />
    </>
  );
}

/** Where the eyes are after the face's steps: each eye's middle and their size. */
interface Eyes {
  lx: number;
  rx: number;
  y: number;
  s: number;
}

export interface Ctx {
  f: Face;
  /** Every area's color, the character's resolved against the team's (`figureColors`). */
  c: Record<FaceArea, string>;
  chin: number;
  eyes: Eyes;
  /** A prefix for this drawing's clip paths, unique on the page. */
  uid: string;
}

/** A painted region: its fill and the area a paint can fills when it is tapped. */
export const paint = (c: Record<FaceArea, string>, slot: FaceArea, shade?: number) => ({
  fill: shade === undefined ? c[slot] : darken(c[slot], shade),
  'data-slot': slot,
});

/** A detail with no area of its own lets a tap through to what is under it. */
export const THROUGH = { pointerEvents: 'none' as const };

/** A face's drawing context: its parts, its colors resolved against the team's, where its eyes are. */
export function faceCtx(face: Face, team: FigureTeam, uid: string): Ctx {
  const a = face.adjust;
  const gap = a.eyesGap * 1.3;
  return {
    f: face,
    c: figureColors(face, team),
    chin: CHIN[face.head],
    eyes: { lx: 39 - gap, rx: 61 + gap, y: 54 + a.eyesY * 1.6, s: 1 + a.eyesSize * 0.12 },
    uid,
  };
}

/** A prefix for a drawing's clip paths, unique on the page. */
export function useDrawingId(): string {
  return `fa${useId().replace(/[^a-zA-Z0-9]/g, '')}`;
}

export function FaceArt({
  face,
  mood = 'idle',
  size,
  disc,
  team,
  view,
  className,
}: {
  face: Face;
  mood?: FaceMood;
  /** The drawn size; absent fills its box. */
  size?: number;
  /**
   * The team's disc drawn inside the picture, ring and all, with the face
   * clipped to it: the maker's big face and a saved picture. A team's mark
   * draws its own disc and leaves this out.
   */
  disc?: { primary: string; secondary: string };
  /**
   * The team's colors, which an outfit nobody painted wears (`figureColors`);
   * the disc's when there is one.
   */
  team?: FigureTeam;
  /** Part of the box to show, as a `viewBox`: the maker's picker crops to a small part (`FACE_PART_VIEW`). */
  view?: string;
  className?: string;
}) {
  const uid = useDrawingId();
  const ctx = faceCtx(face, team ?? disc ?? NO_TEAM, uid);
  const outfit = faceOutfit(face);
  // NOTHING BEHIND THE FACE but the team's disc: a backdrop here showed only
  // as a sliver at the circle's edge, so it is the team's banner now
  // (PLAN-2026-10-24-team-banners).
  // A face with shoulders draws a little smaller, so they show.
  const framed = ON_SHOULDERS.has(face.neck) || outfit.from === 'body';
  const body = (
    <g transform={framed ? 'translate(4 0) scale(0.92)' : undefined}>
      {disc ? (
        <defs>
          <clipPath id={`${uid}-disc`}>
            <circle cx={50} cy={50} r={47} />
          </clipPath>
        </defs>
      ) : null}
      <FaceDrawing
        ctx={ctx}
        shoulders={outfit.from === 'body' ? OUTFIT_BADGE[outfit.id] : undefined}
      />
    </g>
  );
  return (
    <svg
      className={`fsp-botface${className ? ` ${className}` : ''}`}
      data-state={mood}
      viewBox={view ?? '0 0 100 100'}
      width={size}
      height={size}
      aria-hidden
      focusable="false"
    >
      {disc ? (
        <>
          <circle cx={50} cy={50} r={49} fill={disc.secondary} data-slot="ring" />
          <circle cx={50} cy={50} r={46} fill={disc.primary} data-slot="disc" />
          <g clipPath={`url(#${uid}-disc)`}>{body}</g>
        </>
      ) : (
        body
      )}
    </svg>
  );
}

/**
 * A hat alone (PLAN-2026-10-30-the-weekly-hat §7), perched on a badge: the
 * hat's own drawing, cropped to the crown it sits on, in its own colors. A
 * badge clips a face to its disc, which would cut a tall hat's top off, so the
 * week's hat sits on top of the disc whatever the badge is.
 */
export function HatArt({ hat, width }: { hat: WeeklyHat; width: number }) {
  const uid = useDrawingId();
  const ctx = faceCtx({ ...PERCHED, headwear: hat, colors: hatColors(hat) }, NO_TEAM, uid);
  const drawing = HATS[hat];
  return (
    <svg
      className="fsp-hatart"
      viewBox="8 -6 84 50"
      width={width}
      height={Math.round((width * 50) / 84)}
      aria-hidden
      focusable="false"
    >
      {drawing.back?.(ctx)}
      {drawing.front(ctx)}
    </svg>
  );
}

/** The face a perched hat is drawn for: only its crown is shown. */
const PERCHED: Face = {
  head: 'round',
  ears: 'round',
  hair: 'none',
  eyes: 'round',
  brows: 'thin',
  nose: 'button',
  mouth: 'smile',
  facial: 'none',
  headwear: 'none',
  eyewear: 'none',
  neck: 'none',
  marks: [],
  colors: hatColors('party'),
  adjust: NO_ADJUST,
};

/**
 * The head and everything on it, in the 100 × 100 box: a badge's whole
 * drawing, and the top of a character's. `shoulders` are a chosen outfit's on
 * a badge; `figure` leaves out everything a body takes the place of (the
 * shoulders of a garment and a cape), which `FigureArt` draws on the body.
 */
export function FaceDrawing({
  ctx,
  shoulders,
  figure = false,
}: {
  ctx: Ctx;
  shoulders?: (ctx: Ctx) => ReactNode;
  figure?: boolean;
}) {
  const face = ctx.f;
  const uid = ctx.uid;
  const hat = HATS[face.headwear];
  const cover = hat.cover;
  const headPath = HEADS[face.head];
  return (
    <>
      <defs>
        <clipPath id={`${uid}-head`}>
          <path d={headPath} />
        </clipPath>
        {typeof cover === 'number' ? (
          <clipPath id={`${uid}-hair`}>
            <rect x={-20} y={cover} width={140} height={140} />
          </clipPath>
        ) : null}
      </defs>
      {hat.back ? hat.back(ctx) : null}
      {cover === 'all' ? null : (
        <g clipPath={typeof cover === 'number' ? `url(#${uid}-hair)` : undefined}>
          {HAIR[face.hair].back?.(ctx)}
        </g>
      )}
      {figure ? null : shoulders?.(ctx)}
      {/* A garment at the neck under a chosen outfit is the outfit's to draw. */}
      {figure || (shoulders && GARMENT_NECKS.has(face.neck)) ? null : NECK[face.neck].back?.(ctx)}
      <Ears ctx={ctx} />
      <path
        d={headPath}
        {...paint(ctx.c, 'skin')}
        stroke={face.head === 'robot' ? darken(ctx.c.skin, 0.62) : undefined}
        strokeWidth={face.head === 'robot' ? 2 : undefined}
      />
      {face.head === 'robot' ? <RobotPanel ctx={ctx} /> : null}
      <Marks ctx={ctx} layer="skin" />
      {NECK[face.neck].front?.(ctx)}
      <Facial ctx={ctx} layer="under" />
      <EyesPart ctx={ctx} />
      {EYEWEAR[face.eyewear](ctx)}
      <Brows ctx={ctx} />
      <Nose ctx={ctx} />
      <Mouths ctx={ctx} />
      <Facial ctx={ctx} layer="over" />
      {cover === 'all' ? null : (
        <g clipPath={typeof cover === 'number' ? `url(#${uid}-hair)` : undefined}>
          {HAIR[face.hair].front(ctx)}
        </g>
      )}
      <Marks ctx={ctx} layer="over" />
      {hat.front(ctx)}
    </>
  );
}

// ── Ears ───────────────────────────────────────────────────────────────────

function Ears({ ctx }: { ctx: Ctx }) {
  const { c } = ctx;
  const skin = paint(c, 'skin');
  const inner = { fill: darken(c.skin, 0.84), ...THROUGH };
  switch (ctx.f.ears) {
    case 'small':
      return (
        <>
          <circle cx={20.5} cy={58} r={4} {...skin} />
          <circle cx={79.5} cy={58} r={4} {...skin} />
        </>
      );
    case 'round':
      return (
        <>
          <circle cx={19} cy={58} r={5.5} {...skin} />
          <circle cx={81} cy={58} r={5.5} {...skin} />
          <circle cx={18.5} cy={58} r={2.6} {...inner} />
          <circle cx={81.5} cy={58} r={2.6} {...inner} />
        </>
      );
    case 'big':
      return (
        <>
          <ellipse cx={16} cy={57} rx={8} ry={9} {...skin} />
          <ellipse cx={84} cy={57} rx={8} ry={9} {...skin} />
          <ellipse cx={15.5} cy={57} rx={4} ry={5} {...inner} />
          <ellipse cx={84.5} cy={57} rx={4} ry={5} {...inner} />
        </>
      );
    case 'pointy':
      return (
        <>
          <Both d="M23 48 L6 38 L21 66 Z" {...skin} />
          <Both d="M20 51 L11 44 L20 61 Z" {...inner} />
        </>
      );
    case 'floppy':
      return <Both d="M26 40 Q8 40 9 62 Q11 76 20 72 Q21 58 27 48 Z" {...paint(c, 'skin', 0.88)} />;
    case 'bolts':
      return (
        <>
          <rect
            x={12}
            y={51}
            width={8}
            height={14}
            rx={2}
            fill={METAL}
            stroke="#6b7686"
            strokeWidth={1.2}
          />
          <rect
            x={80}
            y={51}
            width={8}
            height={14}
            rx={2}
            fill={METAL}
            stroke="#6b7686"
            strokeWidth={1.2}
          />
          <circle cx={16} cy={58} r={1.6} fill="#6b7686" />
          <circle cx={84} cy={58} r={1.6} fill="#6b7686" />
        </>
      );
    case 'none':
    default:
      return null;
  }
}

/** A robot's face plate: a seam and two rivets, in its own metal. */
function RobotPanel({ ctx }: { ctx: Ctx }) {
  const line = darken(ctx.c.skin, 0.7);
  return (
    <g {...THROUGH}>
      <path d="M25 34 L75 34" stroke={line} strokeWidth={1} opacity={0.6} />
      <circle cx={25} cy={80} r={1.4} fill={line} />
      <circle cx={75} cy={80} r={1.4} fill={line} />
    </g>
  );
}

// ── Hair ───────────────────────────────────────────────────────────────────

interface HairDrawing {
  /** Behind the head: long hair, a ponytail, an afro's mass. */
  back?: (ctx: Ctx) => ReactNode;
  /** Over the head. */
  front: (ctx: Ctx) => ReactNode;
}

const hairFill = (ctx: Ctx) => paint(ctx.c, 'hair');
/** A few strands in a darker shade, for texture; taps go through to the hair. */
const strands = (ctx: Ctx, d: string) => (
  <path
    d={d}
    stroke={darken(ctx.c.hair, 0.7)}
    strokeWidth={1.1}
    fill="none"
    strokeLinecap="round"
    opacity={0.7}
    {...THROUGH}
  />
);

const SHORT_FRONT = 'M19 52 Q18 22 50 22 Q82 22 81 52 Q72 33 50 33 Q28 33 19 52 Z';
const PARTED_FRONT = 'M19 54 Q18 22 50 22 Q82 22 81 54 Q74 34 50 32 Q26 34 19 54 Z';
/** A parting down the middle, in a darker shade. */
const parted = (ctx: Ctx) => strands(ctx, 'M50 23 L50 31.5');
const SMOOTH_FRONT = 'M19 52 Q18 24 50 24 Q82 24 81 52 Q74 34 50 34 Q26 34 19 52 Z';

const HAIR: Record<Face['hair'], HairDrawing> = {
  none: { front: () => null },
  bald: {
    // A tuft over each ear and the top bare.
    front: (ctx) => (
      <g {...hairFill(ctx)}>
        <path d="M18 60 Q15 42 26 36 Q24 48 27 58 Z" />
        <path d="M82 60 Q85 42 74 36 Q76 48 73 58 Z" />
      </g>
    ),
  },
  buzz: {
    front: (ctx) => (
      <path
        d="M20 50 Q19 24 50 24 Q81 24 80 50 Q74 31 50 30 Q26 31 20 50 Z"
        {...hairFill(ctx)}
        opacity={0.92}
      />
    ),
  },
  short: { front: (ctx) => <path d={SHORT_FRONT} {...hairFill(ctx)} /> },
  side: {
    front: (ctx) => (
      <>
        <path d="M19 52 Q18 23 50 23 Q81 23 81 47 Q68 30 42 37 Q27 41 19 52 Z" {...hairFill(ctx)} />
        {strands(ctx, 'M30 30 Q44 26 62 30')}
      </>
    ),
  },
  swoop: {
    front: (ctx) => (
      <>
        <path
          d="M19 52 Q16 28 36 22 Q46 8 66 13 Q86 20 81 52 Q74 34 58 32 Q36 31 19 52 Z"
          {...hairFill(ctx)}
        />
        {strands(ctx, 'M38 22 Q50 14 66 17')}
      </>
    ),
  },
  spiky: {
    front: (ctx) => (
      <path
        d="M19 52 L22 30 L31 37 L34 18 L43 31 L50 12 L57 31 L66 18 L69 37 L78 30 L81 52 Q71 34 50 34 Q29 34 19 52 Z"
        {...hairFill(ctx)}
        stroke={darken(ctx.c.hair)}
        strokeWidth={1.2}
      />
    ),
  },
  messy: {
    front: (ctx) => (
      <path
        d="M18 53 Q15 27 38 22 L41 12 L48 21 L56 11 L60 22 Q85 25 82 53 Q75 35 61 35 L57 41 L52 34 Q30 33 18 53 Z"
        {...hairFill(ctx)}
      />
    ),
  },
  curly: {
    front: (ctx) => (
      <g {...hairFill(ctx)}>
        {[
          [22, 46],
          [26, 34],
          [35, 26],
          [46, 22],
          [57, 22],
          [67, 26],
          [75, 34],
          [78, 46],
        ].map(([x, y]) => (
          <circle key={`${x}-${y}`} cx={x} cy={y} r={9} />
        ))}
      </g>
    ),
  },
  afro: {
    back: (ctx) => <circle cx={50} cy={47} r={37} {...hairFill(ctx)} />,
    front: (ctx) => (
      <g {...hairFill(ctx)}>
        <path d="M19 54 Q17 21 50 20 Q83 21 81 54 Q74 37 50 37 Q26 37 19 54 Z" />
        {[24, 34, 44, 56, 66, 76].map((x) => (
          <circle key={x} cx={x} cy={x < 30 || x > 70 ? 41 : 36} r={5} />
        ))}
      </g>
    ),
  },
  puffs: {
    back: (ctx) => (
      <g {...hairFill(ctx)}>
        <circle cx={24} cy={24} r={13} />
        <circle cx={76} cy={24} r={13} />
      </g>
    ),
    front: (ctx) => (
      <>
        <path d={PARTED_FRONT} {...hairFill(ctx)} />
        {parted(ctx)}
      </>
    ),
  },
  'flat-top': {
    front: (ctx) => (
      <path
        d="M19 52 L19 28 Q19 14 31 14 L69 14 Q81 14 81 28 L81 52 Q74 34 50 34 Q26 34 19 52 Z"
        {...hairFill(ctx)}
      />
    ),
  },
  mohawk: {
    front: (ctx) => (
      <>
        <path
          d={SMOOTH_FRONT}
          {...hairFill(ctx)}
          opacity={0.35}
          clipPath={`url(#${ctx.uid}-head)`}
        />
        <path d="M41 36 Q38 14 50 3 Q62 14 59 36 Q50 32 41 36 Z" {...hairFill(ctx)} />
        {strands(ctx, 'M50 8 L50 30')}
      </>
    ),
  },
  bun: {
    front: (ctx) => (
      <g {...hairFill(ctx)}>
        <circle cx={50} cy={17} r={10} />
        <path d={SMOOTH_FRONT} />
      </g>
    ),
  },
  'top-knot': {
    front: (ctx) => (
      <>
        <g {...hairFill(ctx)}>
          <circle cx={50} cy={13} r={7} />
          <path d={SMOOTH_FRONT} />
        </g>
        <rect
          x={45}
          y={18}
          width={10}
          height={4}
          rx={2}
          fill={darken(ctx.c.hair, 0.6)}
          {...THROUGH}
        />
      </>
    ),
  },
  ponytail: {
    back: (ctx) => (
      <ellipse cx={84} cy={64} rx={8} ry={17} transform="rotate(-18 84 64)" {...hairFill(ctx)} />
    ),
    front: (ctx) => <path d={SHORT_FRONT} {...hairFill(ctx)} />,
  },
  pigtails: {
    back: (ctx) => (
      <>
        <Both d="M21 44 Q4 52 8 76 Q12 84 18 78 Q15 62 25 52 Z" {...hairFill(ctx)} />
        <Both d="M17 47 L23 52" stroke={darken(ctx.c.hair, 0.55)} strokeWidth={3} {...THROUGH} />
      </>
    ),
    front: (ctx) => (
      <>
        <path d={PARTED_FRONT} {...hairFill(ctx)} />
        {parted(ctx)}
      </>
    ),
  },
  bob: {
    back: (ctx) => (
      <path
        d="M14 80 L15 46 Q15 17 50 17 Q85 17 85 46 L86 80 Q80 85 72 81 L28 81 Q20 85 14 80 Z"
        {...hairFill(ctx)}
      />
    ),
    front: (ctx) => (
      <path
        d="M20 50 Q19 23 50 23 Q81 23 80 50 Q79 42 72 41 L28 41 Q21 42 20 50 Z"
        {...hairFill(ctx)}
      />
    ),
  },
  long: {
    back: (ctx) => (
      <path
        d="M16 46 Q14 17 50 17 Q86 17 84 46 L88 100 L66 100 L66 62 L34 62 L34 100 L12 100 Z"
        {...hairFill(ctx)}
      />
    ),
    front: (ctx) => (
      <>
        <path
          d="M19 58 Q17 23 50 22 Q83 23 81 58 Q78 36 60 32 Q44 30 34 38 Q24 44 19 58 Z"
          {...hairFill(ctx)}
        />
        {strands(ctx, 'M58 26 Q70 30 76 40')}
      </>
    ),
  },
  wavy: {
    back: (ctx) => (
      <path
        d="M16 46 Q14 17 50 17 Q86 17 84 46 Q91 57 84 68 Q91 80 84 94 L66 94 L66 62 L34 62 L34 94 L16 94 Q9 80 16 68 Q9 57 16 46 Z"
        {...hairFill(ctx)}
      />
    ),
    front: (ctx) => (
      <>
        <path d={PARTED_FRONT} {...hairFill(ctx)} />
        {parted(ctx)}
      </>
    ),
  },
  braids: {
    back: (ctx) => (
      <g {...hairFill(ctx)}>
        <path d="M16 50 Q14 19 50 19 Q86 19 84 50 L84 60 L16 60 Z" />
        {[58, 66, 74, 82, 90].map((y) => (
          <g key={y}>
            <ellipse
              cx={18}
              cy={y}
              rx={5}
              ry={4.6}
              stroke={darken(ctx.c.hair, 0.7)}
              strokeWidth={0.8}
            />
            <ellipse
              cx={82}
              cy={y}
              rx={5}
              ry={4.6}
              stroke={darken(ctx.c.hair, 0.7)}
              strokeWidth={0.8}
            />
          </g>
        ))}
      </g>
    ),
    front: (ctx) => (
      <>
        <path d={PARTED_FRONT} {...hairFill(ctx)} />
        {parted(ctx)}
        {strands(ctx, 'M30 32 Q38 27 48 28 M70 32 Q62 27 52 28')}
      </>
    ),
  },
  locs: {
    back: (ctx) => (
      <g {...hairFill(ctx)} stroke={darken(ctx.c.hair, 0.72)} strokeWidth={0.8}>
        <path d="M16 50 Q14 18 50 18 Q86 18 84 50 Z" stroke="none" />
        {[11, 16.5, 22, 78, 83.5, 89].map((x, i) => (
          <rect key={x} x={x - 2.9} y={30} width={5.8} height={i % 3 === 1 ? 64 : 56} rx={2.9} />
        ))}
      </g>
    ),
    front: (ctx) => (
      <g {...hairFill(ctx)}>
        <path d="M19 52 Q18 20 50 20 Q82 20 81 52 Q72 34 50 33 Q28 34 19 52 Z" />
        <g stroke={darken(ctx.c.hair, 0.72)} strokeWidth={0.8}>
          {[21, 26.5, 73.5, 79].map((x) => (
            <rect
              key={x}
              x={x - 2.7}
              y={34}
              width={5.4}
              height={x < 24 || x > 76 ? 34 : 26}
              rx={2.7}
            />
          ))}
        </g>
        {strands(ctx, 'M32 30 Q34 26 40 24 M60 24 Q66 26 68 30 M45 27 L46 23 M55 27 L54 23')}
      </g>
    ),
  },
  mullet: {
    back: (ctx) => (
      <path
        d="M17 48 L15 86 Q24 90 30 84 L30 50 Z M83 48 L85 86 Q76 90 70 84 L70 50 Z"
        {...hairFill(ctx)}
      />
    ),
    front: (ctx) => <path d={SHORT_FRONT} {...hairFill(ctx)} />,
  },
  pompadour: {
    front: (ctx) => (
      <>
        <path
          d="M19 52 Q17 31 29 25 Q32 10 51 9 Q70 9 73 20 Q83 27 81 52 Q74 36 50 34 Q26 36 19 52 Z"
          {...hairFill(ctx)}
        />
        {strands(ctx, 'M33 21 Q47 12 66 16 M37 26 Q50 19 68 22')}
      </>
    ),
  },
  bowl: {
    // Straight across, just above the brows.
    front: (ctx) => (
      <path
        d="M18 57 Q16 19 50 18 Q84 19 82 57 L77 57 Q77 45 72 39 L28 39 Q23 45 23 57 Z"
        {...hairFill(ctx)}
      />
    ),
  },
  'comb-over': {
    // A tuft over each ear and a few strands combed across the top.
    front: (ctx) => (
      <>
        <g {...hairFill(ctx)}>
          <path d="M18 60 Q15 42 26 36 Q24 48 27 58 Z" />
          <path d="M82 60 Q85 42 74 36 Q76 48 73 58 Z" />
        </g>
        <path
          d="M22 46 Q34 24 76 32 M22 41 Q38 22 72 27 M25 37 Q42 21 66 23"
          stroke={ctx.c.hair}
          strokeWidth={2.2}
          strokeLinecap="round"
          fill="none"
          data-slot="hair"
        />
      </>
    ),
  },
  undercut: {
    // One side shaved close, the top swept over to the other.
    front: (ctx) => (
      <>
        <path
          d={SMOOTH_FRONT}
          {...hairFill(ctx)}
          opacity={0.35}
          clipPath={`url(#${ctx.uid}-head)`}
        />
        <path
          d="M30 40 Q26 21 48 18 Q72 16 81 36 L81 52 Q76 34 60 30 Q42 28 30 40 Z"
          {...hairFill(ctx)}
        />
        {strands(ctx, 'M38 24 Q56 18 74 28')}
      </>
    ),
  },
  'high-ponytail': {
    back: (ctx) => (
      <path
        d="M46 18 Q45 7 56 7 Q75 8 80 26 Q83 37 78 47 Q74 33 66 22 Q60 14 54 17 Z"
        {...hairFill(ctx)}
      />
    ),
    front: (ctx) => (
      <>
        <path d={SMOOTH_FRONT} {...hairFill(ctx)} />
        <rect
          x={46}
          y={16}
          width={9}
          height={4}
          rx={2}
          fill={darken(ctx.c.hair, 0.6)}
          {...THROUGH}
        />
      </>
    ),
  },
  twists: {
    front: (ctx) => (
      <g {...hairFill(ctx)}>
        <path d="M19 52 Q18 21 50 20 Q82 21 81 52 Q72 34 50 33 Q28 34 19 52 Z" />
        <g stroke={darken(ctx.c.hair, 0.7)} strokeWidth={0.8}>
          {[200, 218, 236, 254, 272, 290, 308, 326, 344].map((deg) => {
            const t = (deg * Math.PI) / 180;
            const x = 50 + 28 * Math.cos(t);
            const y = 47 + 28 * Math.sin(t);
            return (
              <rect
                key={deg}
                x={x - 2.8}
                y={y - 6}
                width={5.6}
                height={11}
                rx={2.8}
                transform={`rotate(${deg + 90} ${x} ${y})`}
              />
            );
          })}
        </g>
      </g>
    ),
  },
  curtains: {
    // Parted down the middle, falling to either side of the brow.
    front: (ctx) => (
      <>
        <path
          d="M19 58 Q16 24 50 22 Q84 24 81 58 Q77 41 65 35 Q56 31 51 25 Q49 31 35 35 Q23 41 19 58 Z"
          {...hairFill(ctx)}
        />
        {strands(ctx, 'M50 23 L50 27 M40 30 Q30 35 25 46 M60 30 Q70 35 75 46')}
      </>
    ),
  },
};

// ── Eyes ───────────────────────────────────────────────────────────────────

function EyesPart({ ctx }: { ctx: Ctx }) {
  const { lx, rx, y, s } = ctx.eyes;
  // One eye, larger, in the middle.
  if (ctx.f.eyes === 'cyclops')
    return (
      <g className="fsp-bf-eyes" data-slot="eyes">
        <g transform={`translate(50 ${y - 3}) scale(${s * 1.3})`}>
          <Eye ctx={ctx} side={1} />
        </g>
      </g>
    );
  const one = (x: number, side: 1 | -1) => (
    <g key={side} transform={`translate(${x} ${y}) scale(${s})`}>
      <Eye ctx={ctx} side={side} />
    </g>
  );
  return (
    <g className="fsp-bf-eyes" data-slot="eyes">
      {one(lx, -1)}
      {one(rx, 1)}
    </g>
  );
}

/** One eye around (0, 0); `side` is −1 for the left and 1 for the right, for what is not symmetric. */
function Eye({ ctx, side }: { ctx: Ctx; side: 1 | -1 }) {
  const iris = ctx.c.eyes;
  const glint = <circle cx={-1.3} cy={-1.5} r={1.1} fill="#fff" opacity={0.85} {...THROUGH} />;
  const white = { fill: '#fff', stroke: INK, strokeWidth: 1.5 };
  switch (ctx.f.eyes) {
    case 'dots':
      return <circle className="fsp-bf-pupil" r={2.7} fill={iris} />;
    case 'wide':
      return (
        <>
          <circle r={7} {...white} />
          <circle className="fsp-bf-pupil" cy={1} r={3.6} fill={iris} />
          <circle cx={-1} cy={0} r={1} fill="#fff" {...THROUGH} />
        </>
      );
    case 'big':
      return (
        <>
          <ellipse rx={7.2} ry={8.4} {...white} />
          <circle className="fsp-bf-pupil" cy={1} r={5} fill={iris} />
          <circle cy={1} r={2.3} fill={INK} {...THROUGH} />
          <circle cx={-2} cy={-1.6} r={1.8} fill="#fff" {...THROUGH} />
          <circle cx={2} cy={3} r={0.9} fill="#fff" {...THROUGH} />
        </>
      );
    case 'almond':
      return (
        <>
          <path d="M-7.5 0 Q0 -6.5 7.5 0 Q0 5.5 -7.5 0 Z" {...white} strokeWidth={1.3} />
          <circle className="fsp-bf-pupil" cy={-0.2} r={2.7} fill={iris} />
          <circle cy={-0.2} r={1.2} fill={INK} {...THROUGH} />
        </>
      );
    case 'lashes':
      return (
        <>
          <circle className="fsp-bf-pupil" r={4} fill={iris} />
          {glint}
          <path
            d={`M${3 * side} -2.8 Q${5.2 * side} -4.4 ${6.6 * side} -3.6 M${3.9 * side} -1.2 Q${6.2 * side} -1.8 ${7.4 * side} -0.6`}
            stroke={INK}
            strokeWidth={1.3}
            strokeLinecap="round"
            fill="none"
          />
        </>
      );
    case 'sleepy':
      return (
        <>
          <path d="M-6 0 Q0 5 6 0 Z" fill={iris} />
          <path d="M-6.5 -0.5 L6.5 -0.5" stroke={INK} strokeWidth={2.2} strokeLinecap="round" />
          <path
            d="M-4 7 Q0 9 4 7"
            stroke={darken(ctx.c.skin, 0.62)}
            strokeWidth={1.2}
            fill="none"
            opacity={0.6}
          />
        </>
      );
    case 'narrow':
      return (
        <>
          <path d="M-6 -0.5 L6 -0.5" stroke={INK} strokeWidth={1.6} strokeLinecap="round" />
          <ellipse className="fsp-bf-pupil" cy={1} rx={3.4} ry={1.8} fill={iris} />
        </>
      );
    case 'happy':
      return (
        <path
          d="M-5 2 Q0 -5 5 2"
          stroke={iris}
          strokeWidth={2.6}
          strokeLinecap="round"
          fill="none"
        />
      );
    case 'wink':
      return side === -1 ? (
        <>
          <circle className="fsp-bf-pupil" r={4.2} fill={iris} />
          {glint}
        </>
      ) : (
        <path
          d="M-5 1 Q0 -4 5 1"
          stroke={INK}
          strokeWidth={2.6}
          strokeLinecap="round"
          fill="none"
        />
      );
    case 'stars':
      return <path d={star(0, 0, 6, 2.6)} fill={iris} stroke={darken(iris, 0.6)} strokeWidth={1} />;
    case 'leds':
      return (
        <rect
          x={-6}
          y={-4.5}
          width={12}
          height={9}
          rx={3}
          fill={iris}
          stroke={darken(iris, 0.45)}
          strokeWidth={1.5}
        />
      );
    case 'closed':
      return (
        <path
          d="M-5 -0.5 Q0 3.8 5 -0.5"
          stroke={INK}
          strokeWidth={2.4}
          strokeLinecap="round"
          fill="none"
        />
      );
    case 'hearts':
      return (
        <path
          className="fsp-bf-pupil"
          d={heart(0, 0.4, 13)}
          fill={iris}
          stroke={darken(iris, 0.65)}
          strokeWidth={0.8}
        />
      );
    case 'dizzy':
      return (
        <path
          transform="translate(-1 0) scale(0.95)"
          d="M0 0 A1 1 0 0 1 2 0 A2 2 0 0 1 -2 0 A3 3 0 0 1 4 0 A4 4 0 0 1 -4 0 A5 5 0 0 1 6 0"
          stroke={iris}
          strokeWidth={1.5}
          strokeLinecap="round"
          fill="none"
        />
      );
    case 'sparkle':
      return (
        <>
          <circle className="fsp-bf-pupil" r={5.4} fill={iris} stroke={INK} strokeWidth={1.2} />
          <circle cy={0.6} r={2.4} fill={INK} {...THROUGH} />
          <circle cx={-2} cy={-2.2} r={2} fill="#fff" {...THROUGH} />
          <circle cx={2.2} cy={2.2} r={1} fill="#fff" {...THROUGH} />
        </>
      );
    case 'side-eye':
      return (
        <>
          <path d="M-7 0 Q0 -5.5 7 0 Q0 5 -7 0 Z" {...white} strokeWidth={1.3} />
          <circle className="fsp-bf-pupil" cx={3.4} cy={0.3} r={2.6} fill={iris} />
          <path
            d="M-7.5 -1.4 Q0 -5.2 7.5 -1.4"
            stroke={INK}
            strokeWidth={2}
            strokeLinecap="round"
            fill="none"
          />
        </>
      );
    case 'cyclops':
      return (
        <>
          <ellipse rx={7.2} ry={8.4} {...white} />
          <circle className="fsp-bf-pupil" cy={1} r={5} fill={iris} />
          <circle cy={1} r={2.3} fill={INK} {...THROUGH} />
          <circle cx={-2} cy={-1.6} r={1.8} fill="#fff" {...THROUGH} />
        </>
      );
    case 'round':
    default:
      return (
        <>
          <circle className="fsp-bf-pupil" r={4.2} fill={iris} />
          {glint}
        </>
      );
  }
}

/** A five-pointed star around (x, y). */
export function star(x: number, y: number, outer: number, inner: number): string {
  const pts: string[] = [];
  for (let i = 0; i < 10; i++) {
    const r = i % 2 === 0 ? outer : inner;
    const t = -Math.PI / 2 + (i * Math.PI) / 5;
    pts.push(`${(x + r * Math.cos(t)).toFixed(2)} ${(y + r * Math.sin(t)).toFixed(2)}`);
  }
  return `M${pts.join(' L')} Z`;
}

/** A heart around (x, y), `w` wide. */
export function heart(x: number, y: number, w: number): string {
  const k = w / 20;
  const p = (dx: number, dy: number) => `${(x + dx * k).toFixed(2)} ${(y + dy * k).toFixed(2)}`;
  return `M${p(0, 7)} C${p(-12, -1)} ${p(-8, -10)} ${p(0, -4)} C${p(8, -10)} ${p(12, -1)} ${p(0, 7)} Z`;
}

// ── Brows ──────────────────────────────────────────────────────────────────

/** Each brow drawn on the left, mirrored for the right unless the right is its own. */
const BROWS: Record<
  Exclude<Face['brows'], 'none'>,
  { d: string; width: number; right?: string }
> = {
  thin: { d: 'M32 43 Q38 40 45 42', width: 2.2 },
  thick: { d: 'M32 43 Q38 40 45 42', width: 3.8 },
  bushy: { d: 'M32 43 Q38 40 45 42', width: 5.2 },
  arched: { d: 'M32 44 Q36 37 45 41', width: 2.8 },
  angled: { d: 'M32 40 L45 44', width: 3.4 },
  worried: { d: 'M32 43 Q38 42 45 38', width: 3 },
  unibrow: { d: 'M31 43 Q40 39 50.5 42', width: 4 },
  raised: { d: 'M32 43 Q38 40 45 42', right: 'M32 38 Q37 32 45 37', width: 3 },
  dots: { d: 'M37.5 41.5 L39.5 41.5', width: 4.6 },
};

function Brows({ ctx }: { ctx: Ctx }) {
  if (ctx.f.brows === 'none') return null;
  const b = BROWS[ctx.f.brows];
  const gap = ctx.f.adjust.eyesGap * 1.3;
  const dy = ctx.f.adjust.eyesY * 1.6 + ctx.f.adjust.browsY * 1.4;
  const line = {
    stroke: ctx.c.brows,
    strokeWidth: b.width,
    strokeLinecap: 'round' as const,
    fill: 'none',
    'data-slot': 'brows',
  };
  return (
    <g className="fsp-bf-brows">
      <g transform={`translate(${-gap} ${dy})`}>
        <path className="fsp-bf-brow-l" d={b.d} {...line} />
      </g>
      <g transform={`translate(${gap} ${dy})`}>
        <path className="fsp-bf-brow-r" d={mx(b.right ?? b.d)} {...line} />
      </g>
    </g>
  );
}

// ── Nose ───────────────────────────────────────────────────────────────────

function Nose({ ctx }: { ctx: Ctx }) {
  const { f, c } = ctx;
  if (f.nose === 'none') return null;
  const dy = f.adjust.noseY * 1.4;
  const s = 1 + f.adjust.noseSize * 0.15;
  const fill = darken(c.skin, 0.84);
  const line = {
    stroke: darken(c.skin, 0.58),
    strokeWidth: 2,
    strokeLinecap: 'round' as const,
    fill: 'none',
  };
  const drawn = (() => {
    switch (f.nose) {
      case 'dot':
        return <ellipse cx={50} cy={62} rx={2} ry={1.6} fill={darken(c.skin, 0.55)} />;
      case 'button':
        return (
          <>
            <circle cx={50} cy={61} r={3.6} fill={fill} />
            <circle cx={49} cy={60} r={1} fill="#fff" opacity={0.35} />
          </>
        );
      case 'round':
        return (
          <>
            <ellipse cx={50} cy={61} rx={5} ry={4.2} fill={fill} />
            <circle cx={47.6} cy={63} r={1} fill={darken(c.skin, 0.5)} />
            <circle cx={52.4} cy={63} r={1} fill={darken(c.skin, 0.5)} />
          </>
        );
      case 'pointy':
        return <path d="M50 53 L44.5 64 L51 64" {...line} />;
      case 'long':
        return <path d="M49 50 Q48 60 46 65 Q50 67.5 53 65" {...line} />;
      case 'wide':
        return (
          <>
            <ellipse cx={50} cy={61.5} rx={6.8} ry={3.9} fill={fill} />
            <circle cx={46.6} cy={62.6} r={1.2} fill={darken(c.skin, 0.5)} />
            <circle cx={53.4} cy={62.6} r={1.2} fill={darken(c.skin, 0.5)} />
          </>
        );
      case 'hook':
        return <path d="M50 51 Q57 60 52.5 65 Q49 66.5 47 64" {...line} />;
      case 'clown':
        return (
          <g data-slot="paint">
            <circle
              cx={50}
              cy={61}
              r={5.5}
              fill={c.paint}
              stroke={darken(c.paint, 0.7)}
              strokeWidth={0.8}
            />
            <circle cx={48} cy={59} r={1.5} fill="#fff" opacity={0.7} {...THROUGH} />
          </g>
        );
      case 'snout':
        return (
          <>
            <ellipse
              cx={50}
              cy={61}
              rx={6.2}
              ry={4.6}
              fill={darken(c.skin, 0.88)}
              stroke={darken(c.skin, 0.62)}
              strokeWidth={1}
            />
            <ellipse cx={47.8} cy={61} rx={1.2} ry={1.9} fill={darken(c.skin, 0.45)} />
            <ellipse cx={52.2} cy={61} rx={1.2} ry={1.9} fill={darken(c.skin, 0.45)} />
          </>
        );
      case 'cat':
        return (
          <>
            <path
              d="M46.4 58.6 L53.6 58.6 L50 62.6 Z"
              fill="#f28fb0"
              stroke="#c4607f"
              strokeWidth={0.7}
              strokeLinejoin="round"
            />
            <path
              d="M50 62.6 L50 64.6"
              stroke={darken(c.skin, 0.5)}
              strokeWidth={1.1}
              strokeLinecap="round"
            />
          </>
        );
    }
  })();
  return (
    <g
      transform={`translate(50 ${61 + dy}) scale(${s}) translate(-50 -61)`}
      {...(f.nose === 'clown' ? {} : THROUGH)}
    >
      {drawn}
    </g>
  );
}

// ── Mouth ──────────────────────────────────────────────────────────────────

/** Every mouth, the state showing one (kit.css). */
function Mouths({ ctx }: { ctx: Ctx }) {
  const a = ctx.f.adjust;
  const dy = a.mouthY * 1.5;
  const s = 1 + a.mouthSize * 0.12;
  return (
    <g transform={`translate(50 ${70 + dy}) scale(${s}) translate(-50 -70)`} {...THROUGH}>
      <g className="fsp-bf-rest">
        <RestMouth ctx={ctx} />
      </g>
      <path
        className="fsp-bf-pleased"
        d="M37 64 Q50 84 63 64 Z"
        fill={MOUTH}
        stroke={INK}
        strokeWidth={1.5}
      />
      <path
        className="fsp-bf-annoyed"
        d="M41 73 Q50 66 59 73"
        stroke={INK}
        strokeWidth={3}
        strokeLinecap="round"
        fill="none"
      />
      <ellipse
        className="fsp-bf-open"
        cx={50}
        cy={70}
        rx={6}
        ry={5}
        fill={MOUTH}
        stroke={INK}
        strokeWidth={1.5}
      />
      {/* Taken aback: a tall O. Asleep: a small soft line. */}
      <ellipse
        className="fsp-bf-o"
        cx={50}
        cy={71}
        rx={4.4}
        ry={6.2}
        fill={MOUTH}
        stroke={INK}
        strokeWidth={1.5}
      />
      <path
        className="fsp-bf-snooze"
        d="M45 70.5 Q50 73.5 55 70.5"
        stroke={INK}
        strokeWidth={2.4}
        strokeLinecap="round"
        fill="none"
      />
    </g>
  );
}

const SMILE = 'M40 67 Q50 76 60 67';

function RestMouth({ ctx }: { ctx: Ctx }) {
  const line = { stroke: INK, strokeWidth: 3, strokeLinecap: 'round' as const, fill: 'none' };
  switch (ctx.f.mouth) {
    case 'grin':
      return (
        <g>
          <path d="M38 65 Q50 80 62 65 Z" fill={MOUTH} stroke={INK} strokeWidth={1.5} />
          <path d="M40 65.5 L60 65.5 L58 69 L42 69 Z" fill="#fff" />
        </g>
      );
    case 'laugh':
      return (
        <g>
          <path d="M35 64 Q50 89 65 64 Z" fill={MOUTH} stroke={INK} strokeWidth={1.5} />
          <path d="M37.5 64.6 L62.5 64.6 L60.5 68.4 L39.5 68.4 Z" fill="#fff" />
          <path d="M43 77.5 Q50 72 57 77.5 Q50 82 43 77.5 Z" fill={TONGUE} />
        </g>
      );
    case 'teeth':
      return (
        <g>
          <path d="M36 65 Q50 82 64 65 Z" fill={MOUTH} stroke={INK} strokeWidth={1.5} />
          <path d="M38 65.5 L42 70 L46 65.5 L50 70 L54 65.5 L58 70 L62 65.5 Z" fill="#fff" />
        </g>
      );
    case 'flat':
      return <path d="M42 70 L58 70" {...line} />;
    case 'smirk':
      return <path d="M41 70 Q52 73 60 65" {...line} />;
    case 'o':
      return (
        <ellipse cx={50} cy={70} rx={4} ry={4.8} fill={MOUTH} stroke={INK} strokeWidth={1.5} />
      );
    case 'tongue':
      return (
        <g>
          <path
            d="M45.5 70 Q45.5 79 50 79 Q54.5 79 54.5 70 Z"
            fill={TONGUE}
            stroke={INK}
            strokeWidth={1.2}
          />
          <path d={SMILE} {...line} />
        </g>
      );
    case 'buck':
      return (
        <g>
          <path
            d="M46.6 70.6 L50 71.4 L50 76 L46.6 76 Z"
            fill="#fff"
            stroke={INK}
            strokeWidth={1}
          />
          <path
            d="M50 71.4 L53.4 70.6 L53.4 76 L50 76 Z"
            fill="#fff"
            stroke={INK}
            strokeWidth={1}
          />
          <path d={SMILE} {...line} />
        </g>
      );
    case 'fangs':
      return (
        <g>
          <path
            d="M42.5 69 L44.3 74 L46 69.9 Z M57.5 69 L55.7 74 L54 69.9 Z"
            fill="#fff"
            stroke={INK}
            strokeWidth={0.9}
          />
          <path d={SMILE} {...line} />
        </g>
      );
    case 'pout':
      return <path d="M43 72.5 Q50 66.5 57 72.5" {...line} strokeWidth={2.8} />;
    case 'grille':
      return (
        <g>
          <rect
            x={38}
            y={65}
            width={24}
            height={10}
            rx={2.5}
            fill="#3a4352"
            stroke={INK}
            strokeWidth={1.2}
          />
          <path
            d="M43 65.5 L43 74.5 M48 65.5 L48 74.5 M52 65.5 L52 74.5 M57 65.5 L57 74.5"
            stroke={METAL}
            strokeWidth={1.4}
          />
        </g>
      );
    case 'braces':
      return (
        <g>
          <path d="M38 65 Q50 80 62 65 Z" fill={MOUTH} stroke={INK} strokeWidth={1.5} />
          <path d="M40 65.5 L60 65.5 L58 69 L42 69 Z" fill="#fff" />
          <path d="M41 67.3 L59 67.3" stroke="#8a94a3" strokeWidth={0.9} />
          {[42.8, 46.6, 50.4, 54.2].map((x) => (
            <rect
              key={x}
              x={x}
              y={66.3}
              width={2}
              height={2}
              rx={0.4}
              fill="#c9ced6"
              stroke="#6b7686"
              strokeWidth={0.35}
            />
          ))}
        </g>
      );
    case 'gap':
      return (
        <g>
          <path d="M38 65 Q50 80 62 65 Z" fill={MOUTH} stroke={INK} strokeWidth={1.5} />
          <path
            d="M40 65.5 L49 65.5 L49 69.4 L42 69 Z M51 65.5 L60 65.5 L58 69 L51 69.4 Z"
            fill="#fff"
          />
        </g>
      );
    case 'gum':
      // A bubble, in face paint; it answers the paint can though the mouth does not.
      return (
        <g data-slot="paint" pointerEvents="visiblePainted">
          <circle
            cx={50}
            cy={71}
            r={6.8}
            fill={ctx.c.paint}
            stroke={darken(ctx.c.paint, 0.72)}
            strokeWidth={1}
          />
          <ellipse cx={47.4} cy={68.6} rx={2} ry={1.3} fill="#fff" opacity={0.7} {...THROUGH} />
        </g>
      );
    case 'whistle':
      return (
        <g>
          <ellipse cx={52} cy={70} rx={2.4} ry={2.7} fill={MOUTH} stroke={INK} strokeWidth={1.4} />
          <ellipse
            cx={61}
            cy={67.5}
            rx={1.7}
            ry={1.25}
            transform="rotate(-20 61 67.5)"
            fill={INK}
          />
          <path
            d="M62.5 67.2 L62.5 60.5 Q65 61.5 65.2 63.6"
            stroke={INK}
            strokeWidth={0.9}
            fill="none"
          />
        </g>
      );
    case 'mouthguard':
      return (
        <g>
          <path d="M38 65 Q50 80 62 65 Z" fill={MOUTH} stroke={INK} strokeWidth={1.5} />
          <path
            d="M39.6 65.4 L60.4 65.4 Q59.6 70.4 50 71 Q40.4 70.4 39.6 65.4 Z"
            fill={ctx.c.paint}
            stroke={darken(ctx.c.paint, 0.7)}
            strokeWidth={0.6}
            data-slot="paint"
            pointerEvents="visiblePainted"
          />
        </g>
      );
    case 'jagged':
      // A carved pumpkin's grin: wide, its teeth square notches.
      return (
        <path
          d="M34 64 Q50 84 66 64 L62 67 L59 64.5 L55 68.5 L52.5 65 L50 69 L47.5 65 L45 68.5 L41 64.5 L38 67 Z"
          fill={MOUTH}
          stroke={INK}
          strokeWidth={1.4}
          strokeLinejoin="round"
        />
      );
    case 'smile':
    default:
      return <path d={SMILE} {...line} />;
  }
}

// ── Beards and mustaches ───────────────────────────────────────────────────

const MUSTACHE = 'M35 66 Q42 57 50 62 Q58 57 65 66 Q58 63 50 65.5 Q42 63 35 66 Z';
const HANDLEBAR =
  'M37 64 Q43 59 50 62.5 Q57 59 63 64 Q69 65 72 58 Q74 67 65 67.5 Q58 65.5 50 66 Q42 65.5 35 67.5 Q26 67 28 58 Q31 65 37 64 Z';

function Facial({ ctx, layer }: { ctx: Ctx; layer: 'under' | 'over' }) {
  const { f, c, chin, uid } = ctx;
  const fill = paint(c, 'facial');
  const dy = f.adjust.mouthY * 1.5;
  if (layer === 'over') {
    if (f.facial === 'mustache')
      return (
        <g transform={`translate(0 ${dy})`}>
          <path d={MUSTACHE} {...fill} stroke={darken(c.facial)} strokeWidth={1} />
        </g>
      );
    if (f.facial === 'handlebar' || f.facial === 'long')
      return (
        <g transform={`translate(0 ${dy})`}>
          <path d={HANDLEBAR} {...fill} stroke={darken(c.facial)} strokeWidth={1} />
        </g>
      );
    if (f.facial === 'pencil')
      return (
        <g transform={`translate(0 ${dy})`}>
          <path
            d="M41 64.6 Q45.5 62.8 50 63.8 Q54.5 62.8 59 64.6"
            stroke={c.facial}
            strokeWidth={1.6}
            strokeLinecap="round"
            fill="none"
            data-slot="facial"
          />
        </g>
      );
    if (f.facial === 'walrus')
      return (
        <g transform={`translate(0 ${dy})`}>
          <path
            d="M32 70 Q33 59 50 61 Q67 59 68 70 Q64.5 66.5 61.5 70.5 Q58.5 66.5 55.5 70.5 Q52.8 66.5 50 70.5 Q47.2 66.5 44.5 70.5 Q41.5 66.5 38.5 70.5 Q35.5 66.5 32 70 Z"
            {...fill}
            stroke={darken(c.facial)}
            strokeWidth={0.8}
          />
        </g>
      );
    return null;
  }
  const atChin = `translate(0 ${chin})`;
  switch (f.facial) {
    case 'stubble':
      return (
        <g clipPath={`url(#${uid}-head)`} opacity={0.3}>
          <path
            d="M17 58 Q23 85 50 85 Q77 85 83 58 L90 100 L10 100 Z"
            {...fill}
            transform={atChin}
          />
          <path d="M38 66 Q50 60 62 66 Q50 64 38 66 Z" {...fill} transform={`translate(0 ${dy})`} />
        </g>
      );
    case 'goatee':
      return (
        <path
          d="M43.5 76 Q50 74 56.5 76 Q57.5 84 50 88 Q42.5 84 43.5 76 Z"
          {...fill}
          transform={atChin}
        />
      );
    case 'soul-patch':
      return (
        <path d="M47.3 76.4 Q50 75.4 52.7 76.4 L51 81.5 L49 81.5 Z" {...fill} transform={atChin} />
      );
    case 'chin-strap':
      return (
        <g clipPath={`url(#${uid}-head)`}>
          <path
            d={HEADS[f.head]}
            fill="none"
            stroke={c.facial}
            strokeWidth={9}
            data-slot="facial"
            clipPath={`url(#${uid}-jaw)`}
          />
          <defs>
            <clipPath id={`${uid}-jaw`}>
              <rect x={0} y={62} width={100} height={60} />
            </clipPath>
          </defs>
        </g>
      );
    case 'full':
      return (
        <path
          d="M21 60 Q22 94 50 94 Q78 94 79 60 Q71 81 50 81 Q29 81 21 60 Z"
          {...fill}
          transform={atChin}
        />
      );
    case 'long':
      return (
        <path
          d="M21 60 Q22 86 34 97 Q42 110 50 113 Q58 110 66 97 Q78 86 79 60 Q71 80 50 81 Q29 80 21 60 Z"
          {...fill}
          transform={atChin}
        />
      );
    case 'chops':
      return (
        <g clipPath={`url(#${uid}-head)`}>
          <Both d="M18 46 L27 46 L28 72 Q22 72 18 64 Z" {...fill} />
        </g>
      );
    default:
      return null;
  }
}

// ── Extras ─────────────────────────────────────────────────────────────────

function Marks({ ctx, layer }: { ctx: Ctx; layer: 'skin' | 'over' }) {
  const { f, c } = ctx;
  const { lx, rx, y } = ctx.eyes;
  const out: ReactNode[] = [];
  for (const m of f.marks) {
    if (layer === 'over') {
      if (m === 'sweat')
        out.push(
          <path
            key={m}
            d="M79 33 Q74 42 79 45 Q84 42 79 33 Z"
            fill="#8fd3ff"
            stroke="#4aa3d8"
            strokeWidth={1}
            {...THROUGH}
          />,
        );
      if (m === 'earrings')
        out.push(
          <g key={m} fill="#f2c230" stroke="#b8901c" strokeWidth={0.6} {...THROUGH}>
            <circle cx={18} cy={64.5} r={1.9} />
            <circle cx={82} cy={64.5} r={1.9} />
          </g>,
        );
      continue;
    }
    switch (m) {
      case 'freckles':
        out.push(
          <g key={m} fill={darken(c.skin, 0.68)} {...THROUGH}>
            {[
              [31, 63],
              [34.5, 66],
              [29, 67],
              [36, 62.5],
              [32.5, 69],
            ].flatMap(([x, yy]) => [
              <circle key={`l${x}`} cx={x} cy={yy} r={0.9} />,
              <circle key={`r${x}`} cx={100 - x!} cy={yy} r={0.9} />,
            ])}
          </g>,
        );
        break;
      case 'blush':
        out.push(
          <g key={m} fill="#ff6f91" opacity={0.32} {...THROUGH}>
            <ellipse cx={30} cy={67} rx={5.5} ry={3.2} />
            <ellipse cx={70} cy={67} rx={5.5} ry={3.2} />
          </g>,
        );
        break;
      case 'dimples':
        out.push(
          <Both
            key={m}
            d="M36.5 69.5 Q37 72.5 39 72.5"
            stroke={darken(c.skin, 0.6)}
            strokeWidth={1.2}
            fill="none"
            strokeLinecap="round"
            {...THROUGH}
          />,
        );
        break;
      case 'mole':
        out.push(
          <circle key={m} cx={61} cy={73} r={1.1} fill={darken(c.skin, 0.4)} {...THROUGH} />,
        );
        break;
      case 'eye-black':
        out.push(
          <g key={m} fill="#1d1d1d" {...THROUGH}>
            {[lx, rx].map((x) => (
              <path
                key={x}
                d={`M${x - 6.5} ${y + 8} L${x + 6.5} ${y + 8} L${x + 5} ${y + 11.5} L${x - 5} ${y + 11.5} Z`}
              />
            ))}
          </g>,
        );
        break;
      case 'stripes':
        out.push(
          <g key={m} {...paint(c, 'paint')}>
            <Both d="M23 63.5 L35 61.5 L35 64 L23 66 Z" />
            <Both d="M23.5 68 L35.5 66 L35.5 68.5 L23.5 70.5 Z" />
          </g>,
        );
        break;
      case 'star':
        out.push(
          <path
            key={m}
            d={star(29, 69, 4.4, 1.9)}
            {...paint(c, 'paint')}
            stroke="#fff"
            strokeWidth={0.8}
          />,
        );
        break;
      case 'heart':
        out.push(
          <path
            key={m}
            d={heart(71, 68, 8)}
            {...paint(c, 'paint')}
            stroke="#fff"
            strokeWidth={0.7}
          />,
        );
        break;
      case 'bandage':
        out.push(
          <g key={m} transform="rotate(-25 69 66)" {...THROUGH}>
            <rect
              x={62}
              y={63.5}
              width={14}
              height={5}
              rx={2.2}
              fill="#f1d1a8"
              stroke="#c9a57e"
              strokeWidth={0.7}
            />
            <rect x={66.5} y={63.5} width={5} height={5} fill="#e6bf94" />
          </g>,
        );
        break;
      case 'sparkles':
        out.push(
          <g key={m} fill="#fff6b8" stroke="#e0b03a" strokeWidth={0.5} {...THROUGH}>
            {(
              [
                [27, 60, 3.2],
                [76, 73, 2.6],
                [78, 61, 1.8],
              ] as const
            ).map(([x, y0, r]) => (
              <path
                key={x}
                d={`M${x} ${y0 - r} L${x + r * 0.28} ${y0 - r * 0.28} L${x + r} ${y0} L${x + r * 0.28} ${y0 + r * 0.28} L${x} ${y0 + r} L${x - r * 0.28} ${y0 + r * 0.28} L${x - r} ${y0} L${x - r * 0.28} ${y0 - r * 0.28} Z`}
              />
            ))}
          </g>,
        );
        break;
      case 'bolt':
        out.push(
          <path
            key={m}
            d="M31.5 62 L27 69.5 L30.6 69.5 L28.6 76 L34.4 67.4 L30.8 67.4 L33 62 Z"
            {...paint(c, 'paint')}
            stroke="#fff"
            strokeWidth={0.6}
            strokeLinejoin="round"
          />,
        );
        break;
      case 'daisy':
        out.push(
          <g key={m}>
            <g {...paint(c, 'paint')} stroke={darken(c.paint, 0.8)} strokeWidth={0.3}>
              {[0, 60, 120, 180, 240, 300].map((deg) => (
                <ellipse
                  key={deg}
                  cx={74}
                  cy={61.3}
                  rx={1.3}
                  ry={2}
                  transform={`rotate(${deg} 74 63.5)`}
                />
              ))}
            </g>
            <circle cx={74} cy={63.5} r={1.4} fill="#f2c230" {...THROUGH} />
          </g>,
        );
        break;
      case 'web':
        // A spider web painted at the temple.
        out.push(
          <g
            key={m}
            stroke={c.paint}
            strokeWidth={0.8}
            fill="none"
            strokeLinecap="round"
            data-slot="paint"
          >
            <path d="M24 58 L38 62 M24 58 L36 72 M24 58 L27 75" />
            <path d="M30.3 59.8 Q28.6 61.6 29.4 64.3 Q27 64.5 25.35 65.65" />
            <path d="M35.9 61.4 Q32.9 65 34.2 69.9 Q29.8 70.4 26.55 72.45" />
          </g>,
        );
        break;
      case 'snowflake':
        out.push(
          <g key={m} stroke={c.paint} strokeWidth={1.1} strokeLinecap="round" data-slot="paint">
            {[0, 60, 120].map((deg) => (
              <path
                key={deg}
                d="M30 64 L30 74 M28.4 65.6 L30 67 L31.6 65.6 M28.4 72.4 L30 71 L31.6 72.4"
                transform={`rotate(${deg} 30 69)`}
              />
            ))}
          </g>,
        );
        break;
      case 'shamrock':
        out.push(
          <g key={m} {...paint(c, 'paint')} stroke="#fff" strokeWidth={0.5}>
            {[0, 120, 240].map((deg) => (
              <path key={deg} d={heart(70, 65.6, 6.5)} transform={`rotate(${deg} 70 68)`} />
            ))}
            <path d="M70 68 Q71 71 72.5 72.5" stroke={c.paint} strokeWidth={1.1} fill="none" />
          </g>,
        );
        break;
      case 'whiskers':
        out.push(
          <Both
            key={m}
            d="M35 66 L22 64 M35 68.5 L22 69.5 M35 71 L23 74.5"
            stroke={darken(c.skin, 0.42)}
            strokeWidth={1}
            strokeLinecap="round"
            {...THROUGH}
          />,
        );
        break;
    }
  }
  return out.length ? <>{out}</> : null;
}

// ── Glasses ────────────────────────────────────────────────────────────────

function EyewearDrawing({
  ctx,
  lens,
  clear = true,
  arms = true,
  width = 2.4,
}: {
  ctx: Ctx;
  /** One lens around (0, 0); `side` −1 left, 1 right. */
  lens: (side: 1 | -1) => string;
  /** Clear lenses are a light tint; dark ones are the lens color itself. */
  clear?: boolean;
  arms?: boolean;
  width?: number;
}) {
  const { c } = ctx;
  const { lx, rx, y } = ctx.eyes;
  const frame = { stroke: c.frames, strokeWidth: width, strokeLinejoin: 'round' as const };
  const glass = { fill: c.lenses, fillOpacity: clear ? 0.28 : 0.95 };
  return (
    <g>
      {arms ? (
        <path
          d={`M${lx - 8} ${y - 1} L19 ${y - 3} M${rx + 8} ${y - 1} L81 ${y - 3}`}
          {...frame}
          fill="none"
          strokeWidth={width * 0.8}
          data-slot="frames"
        />
      ) : null}
      <path
        d={`M${lx + 7} ${y - 1.5} Q50 ${y - 4.5} ${rx - 7} ${y - 1.5}`}
        {...frame}
        fill="none"
        data-slot="frames"
      />
      {([-1, 1] as const).map((side) => (
        <g key={side} transform={`translate(${side === -1 ? lx : rx} ${y})`}>
          <path d={lens(side)} {...glass} data-slot="lenses" />
          <path d={lens(side)} fill="none" {...frame} data-slot="frames" />
          {clear ? null : (
            <path
              d="M-5 -3 L-1 -3"
              stroke="#fff"
              strokeWidth={1.4}
              strokeLinecap="round"
              opacity={0.55}
              {...THROUGH}
            />
          )}
        </g>
      ))}
    </g>
  );
}

const circleLens = (r: number) => () =>
  `M${-r} 0 A${r} ${r} 0 1 0 ${r} 0 A${r} ${r} 0 1 0 ${-r} 0 Z`;
const rectLens = (w: number, h: number, r: number) => () =>
  `M${-w / 2 + r} ${-h / 2} L${w / 2 - r} ${-h / 2} Q${w / 2} ${-h / 2} ${w / 2} ${-h / 2 + r} L${w / 2} ${h / 2 - r} Q${w / 2} ${h / 2} ${w / 2 - r} ${h / 2} L${-w / 2 + r} ${h / 2} Q${-w / 2} ${h / 2} ${-w / 2} ${h / 2 - r} L${-w / 2} ${-h / 2 + r} Q${-w / 2} ${-h / 2} ${-w / 2 + r} ${-h / 2} Z`;

const EYEWEAR: Record<Face['eyewear'], (ctx: Ctx) => ReactNode> = {
  none: () => null,
  round: (ctx) => <EyewearDrawing ctx={ctx} lens={circleLens(8.5)} />,
  square: (ctx) => <EyewearDrawing ctx={ctx} lens={rectLens(18, 14, 3)} />,
  'cat-eye': (ctx) => (
    <EyewearDrawing
      ctx={ctx}
      lens={(side) => {
        const k = -side;
        return `M${-9 * k} -3 Q${-9 * k} -8 ${-3 * k} -7 L${10 * k} -9.5 Q${10.5 * k} 2 ${4 * k} 6 L${-5 * k} 6 Q${-10 * k} 4 ${-9 * k} -3 Z`;
      }}
    />
  ),
  big: (ctx) => <EyewearDrawing ctx={ctx} lens={circleLens(11)} width={2} />,
  readers: (ctx) => (
    <EyewearDrawing
      ctx={ctx}
      lens={() => 'M-8.5 1.5 L8.5 1.5 Q8.5 8.5 0 8.5 Q-8.5 8.5 -8.5 1.5 Z'}
      width={2}
    />
  ),
  shades: (ctx) => (
    <EyewearDrawing
      ctx={ctx}
      clear={false}
      lens={() => 'M-10 -6 L10 -6 L9 3 Q8 8 0 8 Q-8 8 -9 3 Z'}
    />
  ),
  sport: (ctx) => {
    const { y } = ctx.eyes;
    return (
      <g>
        <path
          d={`M14 ${y - 4} Q50 ${y - 12} 86 ${y - 4} L84 ${y + 4} Q66 ${y + 9} 50 ${y + 2} Q34 ${y + 9} 16 ${y + 4} Z`}
          fill={ctx.c.lenses}
          stroke={ctx.c.frames}
          strokeWidth={2.5}
          strokeLinejoin="round"
          data-slot="lenses"
        />
        <path
          d={`M24 ${y - 4} L36 ${y - 6}`}
          stroke="#fff"
          strokeWidth={1.5}
          strokeLinecap="round"
          opacity={0.5}
          {...THROUGH}
        />
      </g>
    );
  },
  goggles: (ctx) => {
    const { lx, rx, y } = ctx.eyes;
    return (
      <g>
        <path
          d={`M12 ${y} L88 ${y}`}
          stroke={darken(ctx.c.frames, 0.55)}
          strokeWidth={5}
          data-slot="frames"
        />
        {[lx, rx].map((x) => (
          <g key={x}>
            <rect
              x={x - 10}
              y={y - 8}
              width={20}
              height={16}
              rx={7}
              fill={ctx.c.lenses}
              fillOpacity={0.85}
              stroke={ctx.c.frames}
              strokeWidth={3}
              data-slot="lenses"
            />
            <path
              d={`M${x - 6} ${y - 4} L${x - 2} ${y - 4}`}
              stroke="#fff"
              strokeWidth={1.5}
              strokeLinecap="round"
              opacity={0.7}
              {...THROUGH}
            />
          </g>
        ))}
      </g>
    );
  },
  '3d': (ctx) => {
    const { lx, rx, y } = ctx.eyes;
    return (
      <g>
        <path
          d={`M${lx - 9} ${y - 1} L19 ${y - 3} M${rx + 9} ${y - 1} L81 ${y - 3} M${lx + 9} ${y - 2} L${rx - 9} ${y - 2}`}
          stroke={ctx.c.frames}
          strokeWidth={2.2}
          data-slot="frames"
        />
        <rect
          x={lx - 9}
          y={y - 7}
          width={18}
          height={14}
          rx={2}
          fill="#e53935"
          fillOpacity={0.75}
          stroke={ctx.c.frames}
          strokeWidth={2.4}
          data-slot="frames"
        />
        <rect
          x={rx - 9}
          y={y - 7}
          width={18}
          height={14}
          rx={2}
          fill="#1e88e5"
          fillOpacity={0.75}
          stroke={ctx.c.frames}
          strokeWidth={2.4}
          data-slot="frames"
        />
      </g>
    );
  },
  monocle: (ctx) => {
    const { rx, y } = ctx.eyes;
    return (
      <g data-slot="frames">
        <circle
          cx={rx}
          cy={y}
          r={8}
          fill="#fff"
          fillOpacity={0.2}
          stroke={ctx.c.frames}
          strokeWidth={2.2}
        />
        <path
          d={`M${rx + 5} ${y + 6} Q86 ${y + 20} 80 ${y + 38}`}
          stroke={ctx.c.frames}
          strokeWidth={1.1}
          fill="none"
        />
      </g>
    );
  },
  patch: (ctx) => {
    const { lx, y } = ctx.eyes;
    return (
      <g data-slot="frames">
        <path
          d={`M18 ${y + 9} L${lx - 6} ${y + 2} M${lx + 5} ${y - 5} Q60 ${y - 16} 82 ${y - 18}`}
          stroke={ctx.c.frames}
          strokeWidth={1.6}
          fill="none"
        />
        <ellipse cx={lx} cy={y} rx={8} ry={7} fill={ctx.c.frames} />
      </g>
    );
  },
  hearts: (ctx) => (
    <EyewearDrawing ctx={ctx} clear={false} lens={() => heart(0, 0.5, 22)} width={2} />
  ),
  stars: (ctx) => (
    <EyewearDrawing ctx={ctx} clear={false} lens={() => star(0, 0, 11, 5.2)} width={2} />
  ),
  aviators: (ctx) => {
    const { lx, rx, y } = ctx.eyes;
    return (
      <g>
        <EyewearDrawing
          ctx={ctx}
          clear={false}
          width={1.6}
          lens={(side) => {
            const k = -side;
            return `M${-10 * k} -5 Q0 -6.5 ${10 * k} -5 Q${11 * k} 3 ${4 * k} 8 Q${-2 * k} 10 ${-7 * k} 6 Q${-11 * k} 2 ${-10 * k} -5 Z`;
          }}
        />
        <path
          d={`M${lx + 7} ${y - 5.5} L${rx - 7} ${y - 5.5}`}
          stroke={ctx.c.frames}
          strokeWidth={1.3}
          data-slot="frames"
        />
      </g>
    );
  },
  snorkel: (ctx) => {
    // A mask over the eyes and nose, its tube up the side.
    const { y } = ctx.eyes;
    return (
      <g>
        <path
          d={`M12 ${y - 1} L24 ${y - 1} M76 ${y - 1} L88 ${y - 1}`}
          stroke={darken(ctx.c.frames, 0.6)}
          strokeWidth={3.5}
          data-slot="frames"
        />
        <path
          d={`M24 ${y - 6} Q24 ${y - 10} 30 ${y - 10} L70 ${y - 10} Q76 ${y - 10} 76 ${y - 6} L76 ${y + 4} Q76 ${y + 9} 69 ${y + 9} L58 ${y + 9} Q54 ${y + 17} 50 ${y + 17} Q46 ${y + 17} 42 ${y + 9} L31 ${y + 9} Q24 ${y + 9} 24 ${y + 4} Z`}
          fill={ctx.c.lenses}
          fillOpacity={0.5}
          stroke={ctx.c.frames}
          strokeWidth={3}
          strokeLinejoin="round"
          data-slot="lenses"
        />
        <path
          d={`M30 ${y - 6} L38 ${y - 6}`}
          stroke="#fff"
          strokeWidth={1.5}
          strokeLinecap="round"
          opacity={0.7}
          {...THROUGH}
        />
        <path
          d={`M80 ${y + 2} L80 ${y - 24} Q80 ${y - 30} 74 ${y - 30}`}
          stroke={ctx.c.frames}
          strokeWidth={4}
          strokeLinecap="round"
          fill="none"
          data-slot="frames"
        />
      </g>
    );
  },
};

// ── Hats ───────────────────────────────────────────────────────────────────

interface HatDrawing {
  /** How far down it hides the hair: a line to clip at, all of it, or none. */
  cover: number | 'all' | null;
  /** Behind the head: a hood, a scarf's drape. */
  back?: (ctx: Ctx) => ReactNode;
  front: (ctx: Ctx) => ReactNode;
}

const hat = (ctx: Ctx, shade?: number) => paint(ctx.c, 'hat', shade);
const trim = (ctx: Ctx, shade?: number) => paint(ctx.c, 'hatTrim', shade);

const HATS: Record<Face['headwear'], HatDrawing> = {
  none: { cover: null, front: () => null },
  cap: {
    cover: 40,
    front: (ctx) => (
      <g>
        <path d="M20 41 Q20 15 50 15 Q80 15 80 41 Z" {...hat(ctx)} />
        <path d="M50 41 L90 41 Q88 48 52 46 Z" {...trim(ctx)} />
        <circle cx={42} cy={28} r={4.5} fill="#f4f1de" {...THROUGH} />
        <circle cx={50} cy={15.5} r={2} {...trim(ctx)} />
      </g>
    ),
  },
  'cap-back': {
    cover: 40,
    front: (ctx) => (
      <g>
        <path d="M20 41 Q20 15 50 15 Q80 15 80 41 Z" {...hat(ctx)} />
        <path d="M22 40 Q8 40 4 35 Q12 31 24 34 Z" {...trim(ctx)} />
        <path
          d="M42 41 Q50 32 58 41 Z"
          fill={ctx.f.hair === 'none' ? ctx.c.skin : ctx.c.hair}
          {...THROUGH}
        />
        <path
          d="M43 37.5 L57 37.5"
          stroke={darken(ctx.c.hat, 0.6)}
          strokeWidth={1.5}
          {...THROUGH}
        />
      </g>
    ),
  },
  visor: {
    cover: null,
    front: (ctx) => (
      <g>
        <path
          d="M19 41 Q50 31 81 41"
          stroke={ctx.c.hat}
          strokeWidth={4}
          fill="none"
          data-slot="hat"
        />
        <path d="M23 41 Q50 34 77 41 Q75 51 50 49 Q25 51 23 41 Z" {...trim(ctx)} />
      </g>
    ),
  },
  beanie: {
    cover: 40,
    front: (ctx) => (
      <g>
        <path d="M19 44 Q19 13 50 13 Q81 13 81 44 Z" {...hat(ctx)} />
        <rect x={18} y={37} width={64} height={10} rx={4.5} {...trim(ctx)} />
        <path
          d="M26 38 L26 46 M34 38 L34 46 M42 38 L42 46 M50 38 L50 46 M58 38 L58 46 M66 38 L66 46 M74 38 L74 46"
          stroke={darken(ctx.c.hatTrim, 0.8)}
          strokeWidth={1}
          {...THROUGH}
        />
      </g>
    ),
  },
  'pom-beanie': {
    cover: 40,
    front: (ctx) => (
      <g>
        <path d="M19 44 Q19 15 50 15 Q81 15 81 44 Z" {...hat(ctx)} />
        <path
          d="M28 20 L72 20 M22 30 L78 30"
          stroke={ctx.c.hatTrim}
          strokeWidth={3}
          data-slot="hatTrim"
        />
        <rect x={18} y={37} width={64} height={10} rx={4.5} {...trim(ctx)} />
        <circle cx={50} cy={11} r={7.5} {...trim(ctx)} />
      </g>
    ),
  },
  propeller: {
    cover: 40,
    front: (ctx) => (
      <g>
        <path d="M20 41 Q20 16 50 16 Q80 16 80 41 Z" {...hat(ctx)} />
        <path
          d="M43.5 16.6 Q41 28 41 41 L59 41 Q59 28 56.5 16.6 Q50 15.4 43.5 16.6 Z"
          {...trim(ctx)}
        />
        <path d="M28 41 Q50 47 72 41 Q50 44 28 41 Z" {...hat(ctx, 0.8)} />
        <path d="M50 16 L50 9" stroke="#555" strokeWidth={1.6} {...THROUGH} />
        <g className="fsp-bf-spin" {...THROUGH}>
          <path d="M50 9 Q38 3 33 8 Q38 12 50 9 Z" fill="#e53935" />
          <path d="M50 9 Q62 15 67 10 Q62 6 50 9 Z" fill="#1e88e5" />
          <circle cx={50} cy={9} r={1.9} fill="#f2c230" />
        </g>
      </g>
    ),
  },
  bucket: {
    cover: 42,
    front: (ctx) => (
      <g>
        <path d="M24 36 Q24 17 50 17 Q76 17 76 36 Z" {...hat(ctx)} />
        <path d="M23 32 L77 32 L77 37 L23 37 Z" {...trim(ctx)} />
        <path d="M12 45 Q17 34 50 35 Q83 34 88 45 Q50 49 12 45 Z" {...hat(ctx, 0.88)} />
      </g>
    ),
  },
  cowboy: {
    cover: 38,
    front: (ctx) => (
      <g>
        <path d="M30 35 Q27 11 40 13 Q50 19 60 13 Q73 11 70 35 Z" {...hat(ctx)} />
        <path d="M29.5 29 L70.5 29 L70.5 34 L29.5 34 Z" {...trim(ctx)} />
        <path d="M3 30 Q12 42 50 37 Q88 42 97 30 Q93 47 50 45 Q7 47 3 30 Z" {...hat(ctx, 0.85)} />
      </g>
    ),
  },
  'top-hat': {
    cover: 36,
    front: (ctx) => (
      <g>
        <path d="M32 7 L68 7 L66 35 L34 35 Z" {...hat(ctx)} />
        <path d="M33.6 27 L66.4 27 L66 32 L34 32 Z" {...trim(ctx)} />
        <path d="M18 37 Q50 30 82 37 Q82 41.5 50 39.5 Q18 41.5 18 37 Z" {...hat(ctx, 0.85)} />
      </g>
    ),
  },
  chef: {
    cover: 36,
    front: (ctx) => (
      <g>
        <g {...hat(ctx)} stroke={darken(ctx.c.hat, 0.85)} strokeWidth={1}>
          <circle cx={35} cy={17} r={10} />
          <circle cx={50} cy={10} r={11} />
          <circle cx={65} cy={17} r={10} />
        </g>
        <rect
          x={28}
          y={20}
          width={44}
          height={18}
          rx={3}
          {...hat(ctx)}
          stroke={darken(ctx.c.hat, 0.85)}
          strokeWidth={1}
        />
        <path
          d="M38 23 L38 36 M50 22 L50 36 M62 23 L62 36"
          stroke={darken(ctx.c.hat, 0.8)}
          strokeWidth={1}
          {...THROUGH}
        />
      </g>
    ),
  },
  crown: {
    cover: null,
    front: (ctx) => (
      <g>
        <path
          d="M24 37 L22 14 L34 26 L42 8 L50 22 L58 8 L66 26 L78 14 L76 37 Z"
          {...hat(ctx)}
          stroke={darken(ctx.c.hat, 0.7)}
          strokeWidth={1.2}
          strokeLinejoin="round"
        />
        <circle cx={50} cy={31} r={3} {...trim(ctx)} />
        <circle cx={36} cy={32} r={2.2} {...trim(ctx)} />
        <circle cx={64} cy={32} r={2.2} {...trim(ctx)} />
      </g>
    ),
  },
  // ── Earned by playing (PLAN-2026-10-24-faces-round-three §5) ──
  'champion-crown': {
    // A crown with a band of jewels and a star between its middle points: the
    // league's champion's alone.
    cover: null,
    front: (ctx) => (
      <g>
        <path
          d="M24 37 L22 14 L34 26 L42 8 L50 22 L58 8 L66 26 L78 14 L76 37 Z"
          {...hat(ctx)}
          stroke={darken(ctx.c.hat, 0.7)}
          strokeWidth={1.2}
          strokeLinejoin="round"
        />
        <rect x={24} y={31} width={52} height={6.5} rx={2} {...hat(ctx, 0.84)} />
        <path
          d={star(50, 14, 5, 2.2)}
          {...hat(ctx)}
          stroke={darken(ctx.c.hat, 0.7)}
          strokeWidth={0.8}
        />
        <circle cx={50} cy={34.2} r={2.8} {...trim(ctx)} />
        <circle cx={38} cy={34.2} r={2} {...trim(ctx)} />
        <circle cx={62} cy={34.2} r={2} {...trim(ctx)} />
        <g fill="#ffffff" opacity={0.7} {...THROUGH}>
          <circle cx={42} cy={9.5} r={1.2} />
          <circle cx={58} cy={9.5} r={1.2} />
        </g>
      </g>
    ),
  },
  'trophy-hat': {
    // A little cup on a headband, for a perfect week.
    cover: null,
    front: (ctx) => (
      <g>
        <path d="M22 41 Q50 28 78 41 L78 46 Q50 33 22 46 Z" {...trim(ctx)} />
        <path
          d="M40 8 L60 8 L58 20 Q50 28 42 20 Z"
          {...hat(ctx)}
          stroke={darken(ctx.c.hat, 0.7)}
          strokeWidth={1}
        />
        <path
          d="M40.5 10 Q33 11 36 18 L41.5 19 M59.5 10 Q67 11 64 18 L58.5 19"
          stroke={darken(ctx.c.hat, 0.7)}
          strokeWidth={1.6}
          fill="none"
          {...THROUGH}
        />
        <rect x={47.5} y={24} width={5} height={6} {...hat(ctx, 0.84)} />
        <rect x={42} y={29.5} width={16} height={4.5} rx={1} {...hat(ctx, 0.78)} />
        <path d={star(50, 15, 3.4, 1.5)} fill="#ffffff" opacity={0.75} {...THROUGH} />
      </g>
    ),
  },
  tiara: {
    cover: null,
    front: (ctx) => (
      <g>
        <path
          d="M33 34 L38 22 L43 29 L50 14 L57 29 L62 22 L67 34 Q50 27 33 34 Z"
          {...hat(ctx)}
          stroke={darken(ctx.c.hat, 0.7)}
          strokeWidth={1}
          strokeLinejoin="round"
        />
        <circle cx={50} cy={24} r={2.8} {...trim(ctx)} />
      </g>
    ),
  },
  helmet: {
    cover: 'all',
    front: (ctx) => (
      <g>
        <path
          d="M13 62 Q11 15 50 12 Q89 15 87 62 L79 64 Q79 43 50 40 Q21 43 21 64 Z"
          {...hat(ctx)}
          stroke={darken(ctx.c.hat, 0.7)}
          strokeWidth={1}
        />
        <path d="M45.5 12.3 L54.5 12.3 L54 40.2 Q50 40 46 40.2 Z" {...trim(ctx)} />
        <circle cx={16} cy={54} r={3} fill={darken(ctx.c.hat, 0.6)} {...THROUGH} />
        <circle cx={84} cy={54} r={3} fill={darken(ctx.c.hat, 0.6)} {...THROUGH} />
        <g
          stroke="#c9ced6"
          strokeWidth={2.4}
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
          {...THROUGH}
        >
          <path d="M21 62 L31 79 L69 79 L79 62" />
          <path d="M25 70 L75 70" />
          <path d="M40 70 L40 79 M60 70 L60 79" />
        </g>
      </g>
    ),
  },
  'hard-hat': {
    cover: 40,
    front: (ctx) => (
      <g>
        <path d="M22 38 Q22 13 50 13 Q78 13 78 38 Z" {...hat(ctx)} />
        <path d="M46.5 13.5 L53.5 13.5 L53.5 38 L46.5 38 Z" {...hat(ctx, 0.85)} />
        <path d="M13 40 Q50 32 87 40 L87 44 Q50 37 13 44 Z" {...hat(ctx, 0.92)} />
      </g>
    ),
  },
  viking: {
    cover: 40,
    front: (ctx) => (
      <g>
        <path
          d="M22 34 Q5 30 5 10 Q13 24 26 25 Z"
          {...trim(ctx)}
          stroke={darken(ctx.c.hatTrim, 0.7)}
          strokeWidth={1}
        />
        <path
          d="M78 34 Q95 30 95 10 Q87 24 74 25 Z"
          {...trim(ctx)}
          stroke={darken(ctx.c.hatTrim, 0.7)}
          strokeWidth={1}
        />
        <path d="M20 42 Q20 14 50 14 Q80 14 80 42 Z" {...hat(ctx)} />
        <rect x={19} y={36} width={62} height={7} rx={2} {...hat(ctx, 0.75)} />
        <g fill={darken(ctx.c.hat, 0.5)} {...THROUGH}>
          {[27, 39, 50, 61, 73].map((x) => (
            <circle key={x} cx={x} cy={39.5} r={1.2} />
          ))}
        </g>
      </g>
    ),
  },
  wizard: {
    cover: 38,
    front: (ctx) => (
      <g>
        <path d="M25 39 L54 -4 L75 39 Z" {...hat(ctx)} />
        <path d="M12 41 Q50 30 88 41 Q50 49 12 41 Z" {...hat(ctx, 0.8)} />
        <path d={star(48, 24, 4, 1.7)} {...trim(ctx)} />
        <path d={star(60, 12, 2.6, 1.1)} {...trim(ctx)} />
      </g>
    ),
  },
  party: {
    cover: null,
    front: (ctx) => (
      <g transform="rotate(12 50 30)">
        <path d="M38 31 L50 2 L62 31 Z" {...hat(ctx)} />
        <path
          d="M41.5 23 L58.5 23 L57 19.5 L43 19.5 Z M45 15 L55 15 L53.6 11.5 L46.4 11.5 Z"
          {...trim(ctx)}
        />
        <circle cx={50} cy={3} r={3.6} {...trim(ctx)} />
      </g>
    ),
  },
  pirate: {
    cover: 36,
    front: (ctx) => (
      <g>
        <path
          d="M11 37 Q19 13 50 17 Q81 13 89 37 Q70 30 50 38 Q30 30 11 37 Z"
          {...hat(ctx)}
          stroke={ctx.c.hatTrim}
          strokeWidth={1.8}
        />
        <circle cx={50} cy={27} r={3.4} {...trim(ctx)} />
      </g>
    ),
  },
  grad: {
    cover: 34,
    front: (ctx) => (
      <g>
        <path d="M30 27 L30 38 Q50 44 70 38 L70 27 Q50 33 30 27 Z" {...hat(ctx, 0.82)} />
        <path d="M13 24 L50 12 L87 24 L50 36 Z" {...hat(ctx)} />
        <path
          d="M50 24 Q70 26 75 30 L75 43"
          stroke={ctx.c.hatTrim}
          strokeWidth={1.4}
          fill="none"
          data-slot="hatTrim"
        />
        <rect x={73} y={42} width={4} height={8} rx={1} {...trim(ctx)} />
        <circle cx={50} cy={24} r={1.7} {...trim(ctx)} />
      </g>
    ),
  },
  headband: {
    cover: null,
    front: (ctx) => <rect x={19} y={35} width={62} height={7} rx={2} {...hat(ctx)} />,
  },
  bandana: {
    cover: 40,
    front: (ctx) => (
      <g>
        <path
          d="M80 37 Q91 33 93 41 Q86 40 82 43 M82 40 Q90 44 88 52 Q83 46 80 43"
          {...hat(ctx, 0.85)}
        />
        <path d="M19 43 Q19 17 50 17 Q81 17 81 43 Q50 36 19 43 Z" {...hat(ctx)} />
        <g {...trim(ctx)}>
          {[
            [32, 26],
            [44, 22],
            [56, 22],
            [68, 26],
            [38, 33],
            [50, 30],
            [62, 33],
          ].map(([x, y]) => (
            <circle key={`${x}-${y}`} cx={x} cy={y} r={1.6} />
          ))}
        </g>
      </g>
    ),
  },
  hood: {
    cover: null,
    back: (ctx) => (
      <circle cx={50} cy={58} r={44} {...hat(ctx)} stroke={ctx.c.hatTrim} strokeWidth={2} />
    ),
    front: () => null,
  },
  'head-scarf': {
    cover: 'all',
    back: (ctx) => <path d="M9 100 L11 56 Q11 12 50 11 Q89 12 89 56 L91 100 Z" {...hat(ctx)} />,
    front: (ctx) => (
      <g>
        <path
          d="M13 84 Q8 50 17 31 Q29 16 50 16 Q71 16 83 31 Q92 50 87 84 Q81 66 81 56 Q79 34 50 32 Q21 34 19 56 Q19 66 13 84 Z"
          {...hat(ctx)}
        />
        <path
          d="M19 56 Q21 34 50 32 Q79 34 81 56"
          stroke={ctx.c.hatTrim}
          strokeWidth={1.8}
          fill="none"
          data-slot="hatTrim"
        />
      </g>
    ),
  },
  turban: {
    cover: 40,
    front: (ctx) => (
      <g>
        <path d="M17 47 Q12 15 50 11 Q88 15 83 47 Q70 37 50 39 Q30 37 17 47 Z" {...hat(ctx)} />
        <g
          stroke={darken(ctx.c.hat, 0.78)}
          strokeWidth={1.8}
          fill="none"
          strokeLinecap="round"
          {...THROUGH}
        >
          <path d="M21 40 Q38 19 72 21" />
          <path d="M28 44 Q50 25 80 34" />
        </g>
        <circle cx={50} cy={29} r={3} {...trim(ctx)} />
      </g>
    ),
  },
  headphones: {
    cover: null,
    front: (ctx) => (
      <g>
        <path
          d="M16 54 Q16 13 50 13 Q84 13 84 54"
          stroke={ctx.c.hat}
          strokeWidth={5}
          fill="none"
          data-slot="hat"
        />
        <rect x={8} y={45} width={13} height={21} rx={5.5} {...hat(ctx)} />
        <rect x={79} y={45} width={13} height={21} rx={5.5} {...hat(ctx)} />
        <rect x={15} y={47} width={6} height={17} rx={3} {...trim(ctx)} />
        <rect x={79} y={47} width={6} height={17} rx={3} {...trim(ctx)} />
      </g>
    ),
  },
  headset: {
    cover: null,
    front: (ctx) => (
      <g stroke={ctx.c.hat} fill="none" strokeLinecap="round" data-slot="hat">
        <path d="M17 54 Q17 17 50 17 Q83 17 83 54" strokeWidth={4} />
        <rect x={12} y={48} width={9} height={15} rx={3} fill={ctx.c.hat} />
        <path d="M17 62 Q21 77 37 75" strokeWidth={2.5} />
        <circle cx={38} cy={75} r={2.8} fill={ctx.c.hat} />
      </g>
    ),
  },
  horns: {
    cover: null,
    front: (ctx) => (
      <g {...hat(ctx)} stroke={darken(ctx.c.hat, 0.6)} strokeWidth={1.5}>
        <path d="M31 31 L22 10 L40 26 Z" />
        <path d="M69 31 L78 10 L60 26 Z" />
      </g>
    ),
  },
  antenna: {
    cover: null,
    front: (ctx) => (
      <g>
        <path d="M50 26 L50 11" stroke={ctx.c.hat} strokeWidth={3} data-slot="hat" />
        <circle
          cx={50}
          cy={9}
          r={5}
          {...trim(ctx)}
          stroke={darken(ctx.c.hatTrim, 0.65)}
          strokeWidth={1.2}
        />
      </g>
    ),
  },
  halo: {
    cover: null,
    front: (ctx) => (
      <g data-slot="hat" fill="none">
        <ellipse cx={50} cy={10} rx={18} ry={5} stroke={ctx.c.hat} strokeWidth={7} opacity={0.3} />
        <ellipse cx={50} cy={10} rx={18} ry={5} stroke={ctx.c.hat} strokeWidth={3.2} />
      </g>
    ),
  },
  'cat-ears': {
    cover: null,
    front: (ctx) => (
      <g>
        <Both d="M22 36 L23 9 L43 25 Z" {...hat(ctx)} />
        <Both d="M25.5 30 L26 15 L37 24 Z" {...trim(ctx)} />
      </g>
    ),
  },
  'bunny-ears': {
    cover: null,
    front: (ctx) => (
      <g>
        <g transform="rotate(-12 40 28)">
          <path d="M35 30 Q31 -6 40 -8 Q49 -6 45 30 Z" {...hat(ctx)} />
          <path d="M37.5 27 Q35.5 0 40 -2 Q44.5 0 42.5 27 Z" {...trim(ctx)} />
        </g>
        <g transform="rotate(12 60 28)">
          <path d="M55 30 Q51 -6 60 -8 Q69 -6 65 30 Z" {...hat(ctx)} />
          <path d="M57.5 27 Q55.5 0 60 -2 Q64.5 0 62.5 27 Z" {...trim(ctx)} />
        </g>
      </g>
    ),
  },
  bow: {
    cover: null,
    front: (ctx) => (
      <g transform="rotate(-14 66 22)">
        <path d="M66 22 Q52 9 48 19 Q50 30 66 25 Z" {...hat(ctx)} />
        <path d="M66 22 Q80 9 84 19 Q82 30 66 25 Z" {...hat(ctx)} />
        <circle cx={66} cy={23.5} r={3.6} {...hat(ctx, 0.75)} />
      </g>
    ),
  },
  flowers: {
    cover: null,
    front: (ctx) => {
      const spots: [number, number][] = [
        [25, 36],
        [36, 28],
        [50, 25],
        [64, 28],
        [75, 36],
      ];
      return (
        <g>
          <path
            d="M20 40 Q24 26 50 24 Q76 26 80 40"
            stroke={ctx.c.hatTrim}
            strokeWidth={2.4}
            fill="none"
            data-slot="hatTrim"
          />
          {spots.map(([x, y], i) => (
            <g key={x}>
              {[0, 72, 144, 216, 288].map((deg) => (
                <circle
                  key={deg}
                  cx={x + 3 * Math.cos((deg * Math.PI) / 180)}
                  cy={y + 3 * Math.sin((deg * Math.PI) / 180)}
                  r={2.6}
                  {...(i % 2 === 0 ? hat(ctx) : hat(ctx, 0.85))}
                />
              ))}
              <circle cx={x} cy={y} r={1.7} fill="#ffe14d" {...THROUGH} />
            </g>
          ))}
        </g>
      );
    },
  },
  'shades-up': {
    cover: null,
    front: (ctx) => (
      // Pushed up on the hair, the lenses catching the light.
      <g>
        <g {...hat(ctx)}>
          <path d="M28 30 L46 30 Q46 39 37 39 Q28 39 28 30 Z" />
          <path d="M54 30 L72 30 Q72 39 63 39 Q54 39 54 30 Z" />
        </g>
        <path d="M46 31 L54 31" stroke={ctx.c.hat} strokeWidth={2.5} data-slot="hat" />
        <path
          d="M31 32 L36 32 M57 32 L62 32"
          stroke="#fff"
          strokeWidth={1.5}
          opacity={0.7}
          {...THROUGH}
        />
      </g>
    ),
  },
  'sleep-mask': {
    cover: null,
    front: (ctx) => (
      <g>
        <rect
          x={18}
          y={31}
          width={64}
          height={11}
          rx={5.5}
          {...hat(ctx)}
          stroke={darken(ctx.c.hat, 0.7)}
          strokeWidth={1.2}
        />
        <ellipse cx={39} cy={36.5} rx={7} ry={3.5} {...trim(ctx)} />
        <ellipse cx={61} cy={36.5} rx={7} ry={3.5} {...trim(ctx)} />
      </g>
    ),
  },
  beret: {
    cover: 34,
    front: (ctx) => (
      <g transform="rotate(8 52 26)">
        <ellipse cx={52} cy={24} rx={30} ry={11} {...hat(ctx)} />
        <path
          d="M26 29 Q52 37 78 29"
          stroke={darken(ctx.c.hat, 0.72)}
          strokeWidth={1.4}
          fill="none"
          {...THROUGH}
        />
        <rect x={50.5} y={10} width={3} height={4.5} rx={1} {...hat(ctx, 0.8)} />
      </g>
    ),
  },
  fedora: {
    cover: 37,
    front: (ctx) => (
      <g>
        <path d="M29 36 Q27 16 38 14 Q44 18 50 15 Q56 18 62 14 Q73 16 71 36 Z" {...hat(ctx)} />
        <path d="M28.6 29 L71.4 29 L71.2 34.5 L28.8 34.5 Z" {...trim(ctx)} />
        <path d="M10 39 Q50 28 90 39 Q86 44 50 42 Q14 44 10 39 Z" {...hat(ctx, 0.85)} />
      </g>
    ),
  },
  trucker: {
    // A foam front in the hat's color, a mesh back in its trim.
    cover: 40,
    front: (ctx) => (
      <g>
        <path d="M20 41 Q20 15 50 15 Q80 15 80 41 Z" {...trim(ctx)} />
        <g fill={darken(ctx.c.hatTrim, 0.75)} {...THROUGH}>
          {(
            [
              [25, 32],
              [28, 26],
              [25, 38],
              [30, 35],
              [75, 32],
              [72, 26],
              [75, 38],
              [70, 35],
            ] as const
          ).map(([x, y]) => (
            <circle key={`${x}-${y}`} cx={x} cy={y} r={0.8} />
          ))}
        </g>
        <path d="M33 41 Q31 17 50 16 Q69 17 67 41 Z" {...hat(ctx)} />
        <path d="M23 40 Q50 34 77 40 Q75 47.5 50 46.5 Q25 47.5 23 40 Z" {...hat(ctx, 0.85)} />
        <circle cx={50} cy={15.5} r={2} {...hat(ctx)} />
      </g>
    ),
  },
  earmuffs: {
    cover: null,
    front: (ctx) => (
      <g>
        <path
          d="M17 56 Q16 17 50 16 Q84 17 83 56"
          stroke={ctx.c.hatTrim}
          strokeWidth={3}
          fill="none"
          data-slot="hatTrim"
        />
        {[17, 83].map((x) => (
          <g key={x}>
            <circle cx={x} cy={57} r={8.5} {...hat(ctx)} />
            <circle cx={x} cy={57} r={5} {...hat(ctx, 0.9)} />
          </g>
        ))}
      </g>
    ),
  },
  'space-helmet': {
    // A glass bubble over the whole head, a collar at its foot.
    cover: null,
    front: (ctx) => (
      <g>
        <circle
          cx={50}
          cy={54}
          r={41}
          fill="#bfe3ff"
          fillOpacity={0.18}
          stroke="#ffffff"
          strokeOpacity={0.75}
          strokeWidth={2}
          {...THROUGH}
        />
        <path
          d="M22 42 Q27 25 44 18"
          stroke="#fff"
          strokeWidth={3}
          strokeLinecap="round"
          fill="none"
          opacity={0.7}
          {...THROUGH}
        />
        <path
          d="M18 84 Q50 99 82 84 L84 92 Q50 107 16 92 Z"
          {...hat(ctx)}
          stroke={darken(ctx.c.hat, 0.75)}
          strokeWidth={1}
        />
      </g>
    ),
  },
  'sun-hat': {
    cover: 36,
    front: (ctx) => (
      <g>
        <path d="M30 34 Q30 15 50 15 Q70 15 70 34 Z" {...hat(ctx)} />
        <path d="M30 28.5 L70 28.5 L70 34 L30 34 Z" {...trim(ctx)} />
        <path
          d="M5 40 Q9 31 30 32.5 Q50 30.5 70 32.5 Q91 31 95 40 Q89 47 70 43 Q50 46 30 43 Q11 47 5 40 Z"
          {...hat(ctx, 0.92)}
        />
      </g>
    ),
  },
  newsboy: {
    cover: 39,
    front: (ctx) => (
      <g>
        <path d="M18 40 Q14 22 40 17 Q63 13 80 25 Q87 33 82 40 Z" {...hat(ctx)} />
        <path
          d="M46 17 Q40 28 36 40 M46 17 Q58 26 66 40 M46 17 Q30 24 22 38"
          stroke={darken(ctx.c.hat, 0.75)}
          strokeWidth={1}
          fill="none"
          {...THROUGH}
        />
        <circle cx={46} cy={17} r={2.2} {...trim(ctx)} />
        <path d="M26 40 Q50 34 74 40 Q72 46 50 45 Q28 46 26 40 Z" {...trim(ctx)} />
      </g>
    ),
  },
  jester: {
    cover: 38,
    front: (ctx) => (
      <g>
        <Both d="M44 36 Q40 16 30 15 Q18 16 11 33 Q16 34 20 38 Z" {...hat(ctx, 0.8)} />
        <path d="M37 37 Q39 15 50 6 Q61 15 63 37 Z" {...hat(ctx)} />
        <rect x={19} y={33} width={62} height={8} rx={3} {...trim(ctx)} />
        <g fill="#f2c230" stroke="#b8901c" strokeWidth={0.7} {...THROUGH}>
          <circle cx={11} cy={34} r={2.8} />
          <circle cx={89} cy={34} r={2.8} />
          <circle cx={50} cy={6} r={2.8} />
        </g>
      </g>
    ),
  },
  flower: {
    // Tucked behind the ear.
    cover: null,
    front: (ctx) => (
      <g>
        {[0, 72, 144, 216, 288].map((deg) => (
          <ellipse
            key={deg}
            cx={80}
            cy={42.5}
            rx={3}
            ry={5}
            transform={`rotate(${deg} 80 47)`}
            {...hat(ctx)}
            stroke={darken(ctx.c.hat, 0.8)}
            strokeWidth={0.5}
          />
        ))}
        <circle cx={80} cy={47} r={2.8} {...trim(ctx)} />
      </g>
    ),
  },
  // The weekly hat's own two (PLAN-2026-10-30-the-weekly-hat §3), in the
  // face maker's hats too.
  'traffic-cone': {
    cover: 38,
    front: (ctx) => (
      <g transform="rotate(-7 50 36)">
        <path d="M24 34 L76 34 L79 40 L21 40 Z" {...hat(ctx, 0.82)} />
        <path d="M33 34 L45.5 2.5 Q50 0.5 54.5 2.5 L67 34 Z" {...hat(ctx)} />
        <path d="M37.6 22.4 L62.4 22.4 L60 16.4 L40 16.4 Z" {...trim(ctx)} />
        <path d="M42.4 10.4 L57.6 10.4 L56.4 7.4 L43.6 7.4 Z" {...trim(ctx)} />
      </g>
    ),
  },
  lampshade: {
    cover: 36,
    front: (ctx) => (
      <g transform="rotate(6 50 30)">
        <path
          d="M37 4 L63 4 L78 35 L22 35 Z"
          {...hat(ctx)}
          stroke={darken(ctx.c.hat, 0.8)}
          strokeWidth={1}
        />
        <path
          d="M43 4 L36 35 M50 4 L50 35 M57 4 L64 35"
          stroke={darken(ctx.c.hat, 0.86)}
          strokeWidth={0.9}
          fill="none"
          {...THROUGH}
        />
        <path d="M22 35 L78 35 L78 38 L22 38 Z" {...trim(ctx)} />
        <g {...trim(ctx)}>
          {[25, 31, 37, 43, 49, 55, 61, 67, 73].map((x) => (
            <circle key={x} cx={x} cy={40.2} r={1.6} />
          ))}
        </g>
      </g>
    ),
  },

  // ── A season's (PLAN-2026-10-24-faces-round-two §2) ──
  'witch-hat': {
    cover: 38,
    front: (ctx) => (
      <g>
        <path d="M28 39 L44 9 Q48 1 59 2 Q52 6 52.5 13 L72 39 Z" {...hat(ctx)} />
        <path d="M31.2 33 L68.8 33 L71 38.5 L29 38.5 Z" {...trim(ctx)} />
        <rect
          x={45.5}
          y={32.4}
          width={9}
          height={6.6}
          rx={1}
          fill="none"
          stroke="#f2c230"
          strokeWidth={1.4}
          {...THROUGH}
        />
        <path d="M5 42 Q50 30 95 42 Q50 50 5 42 Z" {...hat(ctx, 0.85)} />
      </g>
    ),
  },
  pumpkin: {
    // Sat on the head, its stem and leaf the trim.
    cover: 40,
    front: (ctx) => (
      <g>
        <ellipse cx={50} cy={27} rx={31} ry={17} {...hat(ctx)} />
        <path
          d="M37 11 Q28 27 37 43 M50 10 L50 44 M63 11 Q72 27 63 43"
          stroke={darken(ctx.c.hat, 0.72)}
          strokeWidth={1.4}
          fill="none"
          {...THROUGH}
        />
        <path d="M47.5 11 Q46 4 49.5 1 L53 2 Q50.5 5.5 52.5 11 Z" {...trim(ctx)} />
        <path d="M52 6 Q59 0 65 3 Q59 9 52 6 Z" {...trim(ctx, 0.85)} />
      </g>
    ),
  },
  'bat-headband': {
    cover: null,
    front: (ctx) => (
      <g>
        <path
          d="M19 46 Q19 20 50 19 Q81 20 81 46"
          stroke={ctx.c.hat}
          strokeWidth={3}
          fill="none"
          data-slot="hat"
        />
        <g transform="translate(50 17) scale(1.2) translate(-50 -12)">
          <path
            d="M50 9 L47.5 5 L46.5 10 Q41 5 29 7 Q35 10 34 15 Q39 12.5 42 16 Q45 13.5 50 18 Q55 13.5 58 16 Q61 12.5 66 15 Q65 10 71 7 Q59 5 53.5 10 L52.5 5 Z"
            {...hat(ctx)}
          />
          <circle cx={48} cy={11} r={0.9} fill="#ffe14d" {...THROUGH} />
          <circle cx={52} cy={11} r={0.9} fill="#ffe14d" {...THROUGH} />
        </g>
      </g>
    ),
  },
  turkey: {
    // A roast turkey, drumsticks up.
    cover: 38,
    front: (ctx) => (
      <g>
        {[-12, 12].map((deg) => {
          const x = deg < 0 ? 40 : 60;
          return (
            <g key={deg} transform={`rotate(${deg} ${x} 34)`}>
              <rect x={x - 1.2} y={8} width={2.4} height={7} {...trim(ctx)} />
              <circle cx={x - 1.5} cy={8} r={1.9} {...trim(ctx)} />
              <circle cx={x + 1.5} cy={8} r={1.9} {...trim(ctx)} />
              <path
                d={`M${x - 6} 30 Q${x - 7} 18 ${x - 1.6} 14 L${x + 1.6} 14 Q${x + 7} 18 ${x + 6} 30 Z`}
                {...hat(ctx)}
                stroke={darken(ctx.c.hat, 0.65)}
                strokeWidth={1}
              />
            </g>
          );
        })}
        <path
          d="M19 40 Q19 24 50 24 Q81 24 81 40 Z"
          {...hat(ctx)}
          stroke={darken(ctx.c.hat, 0.65)}
          strokeWidth={1}
        />
        <path
          d="M30 33 Q40 28 54 28.5"
          stroke="#fff"
          strokeWidth={2}
          strokeLinecap="round"
          fill="none"
          opacity={0.35}
          {...THROUGH}
        />
      </g>
    ),
  },
  santa: {
    cover: 40,
    front: (ctx) => (
      <g>
        <path d="M20 40 Q20 12 50 11 Q76 10 87 36 Q80 27 74 27 Q79 33 80 40 Z" {...hat(ctx)} />
        <rect x={17} y={35} width={66} height={10} rx={5} {...trim(ctx)} />
        <circle cx={87} cy={38} r={5.5} {...trim(ctx)} />
      </g>
    ),
  },
  antlers: {
    cover: null,
    front: (ctx) => (
      <g>
        <path
          d="M19 46 Q19 21 50 20 Q81 21 81 46"
          stroke={darken(ctx.c.hat, 0.6)}
          strokeWidth={2.5}
          fill="none"
          {...THROUGH}
        />
        <Both
          d="M35 25 L28 6 M30 12 L21 8 M29 9 L32 2 M32 18 L23 16"
          stroke={ctx.c.hat}
          strokeWidth={3.6}
          strokeLinecap="round"
          fill="none"
          data-slot="hat"
        />
      </g>
    ),
  },
  elf: {
    cover: 40,
    front: (ctx) => (
      <g>
        <path d="M21 41 Q24 22 40 17 Q56 12 70 16 Q66 21 66 28 Q68 35 79 41 Z" {...hat(ctx)} />
        <path
          d="M19 37 L81 37 L81 45 L76 41.5 L70.5 45 L65 41.5 L59.5 45 L54 41.5 L48.5 45 L43 41.5 L37.5 45 L32 41.5 L26.5 45 L21 41.5 L19 45 Z"
          {...trim(ctx)}
        />
        <circle
          cx={71}
          cy={16}
          r={3.2}
          fill="#f2c230"
          stroke="#b8901c"
          strokeWidth={0.8}
          {...THROUGH}
        />
      </g>
    ),
  },
  'heart-boppers': {
    cover: null,
    front: (ctx) => (
      <g>
        <path
          d="M19 46 Q19 21 50 20 Q81 21 81 46"
          stroke={ctx.c.hatTrim}
          strokeWidth={3}
          fill="none"
          data-slot="hatTrim"
        />
        <Both
          d="M41 21 Q38 19.5 38.5 16.5"
          stroke={ctx.c.hatTrim}
          strokeWidth={1.4}
          fill="none"
          data-slot="hatTrim"
        />
        <g {...hat(ctx)} stroke={darken(ctx.c.hat, 0.7)} strokeWidth={0.8}>
          <path d={heart(38, 13, 11)} />
          <path d={heart(62, 13, 11)} />
        </g>
      </g>
    ),
  },
  'shamrock-hat': {
    cover: 36,
    front: (ctx) => (
      <g>
        <path d="M33 10 L67 10 L65 35 L35 35 Z" {...hat(ctx)} />
        <path d="M34.4 26 L65.6 26 L65.2 31.5 L34.8 31.5 Z" {...trim(ctx)} />
        <rect
          x={45.5}
          y={25.4}
          width={9}
          height={6.7}
          rx={1}
          fill="none"
          stroke={darken(ctx.c.hatTrim, 0.6)}
          strokeWidth={1.3}
          {...THROUGH}
        />
        <g fill="#ffffff" opacity={0.85} {...THROUGH}>
          {[0, 120, 240].map((deg) => (
            <path key={deg} d={heart(50, 15.6, 5.5)} transform={`rotate(${deg} 50 17.5)`} />
          ))}
        </g>
        <path d="M18 37 Q50 30 82 37 Q82 41.5 50 39.5 Q18 41.5 18 37 Z" {...hat(ctx, 0.85)} />
      </g>
    ),
  },
};

// ── At the neck ────────────────────────────────────────────────────────────

interface NeckDrawing {
  /** Under the head: shoulders and a collar the chin sits over. */
  back?: (ctx: Ctx) => ReactNode;
  /** Over it: a tie, pearls, a whistle. */
  front?: (ctx: Ctx) => ReactNode;
}

const neck = (ctx: Ctx, shade?: number) => paint(ctx.c, 'neck', shade);
const neckTrim = (ctx: Ctx, shade?: number) => paint(ctx.c, 'neckTrim', shade);
/** Shoulders under the chin, and the neck between, down past the disc's edge. */
const SHOULDERS = 'M2 112 L4 100 Q6 88 26 86 L74 86 Q94 88 96 100 L98 112 Z';
/** What is worn on the shoulders: a face wearing one is drawn a little smaller, so they show. */
const ON_SHOULDERS: ReadonlySet<Face['neck']> = new Set([
  'collar',
  'jersey',
  'hoodie',
  'turtleneck',
  'cape',
  'sweater',
  'overalls',
]);
const skinNeck = (ctx: Ctx) => (
  <rect x={41} y={74} width={18} height={20} {...paint(ctx.c, 'skin', 0.88)} />
);
/** What hangs from the chin moves with its length, a little. */
const low = (ctx: Ctx) => `translate(0 ${Math.min(ctx.chin, 3)})`;

/** The six garments worn at the neck before there was a body (PLAN-2026-10-24-faces-round-three §2.1). */
type Garment = 'collar' | 'jersey' | 'hoodie' | 'turtleneck' | 'overalls' | 'sweater';

/**
 * A garment's shoulders, in either pair of areas: the neck's, for a face that
 * wears it there as it did before there was a body, or the outfit's, for a
 * face that has chosen it as its outfit. One drawing, two places to paint it.
 */
function garment(id: Garment, m: FaceArea, t: FaceArea): (ctx: Ctx) => ReactNode {
  const main = (ctx: Ctx, shade?: number) => paint(ctx.c, m, shade);
  const trim = (ctx: Ctx, shade?: number) => paint(ctx.c, t, shade);
  switch (id) {
    case 'collar':
      return (ctx) => (
        <g transform={low(ctx)}>
          {skinNeck(ctx)}
          <path d={SHOULDERS} {...main(ctx)} />
          <path
            d="M38 85 L50 93 L42 99 Z M62 85 L50 93 L58 99 Z"
            {...main(ctx, 0.9)}
            stroke={darken(ctx.c[m], 0.75)}
            strokeWidth={0.8}
          />
        </g>
      );
    case 'jersey':
      return (ctx) => (
        <g transform={low(ctx)}>
          {skinNeck(ctx)}
          <path
            d="M2 112 L4 100 Q6 88 26 86 L40 85 L50 95 L60 85 L74 86 Q94 88 96 100 L98 112 Z"
            {...main(ctx)}
          />
          <path
            d="M40 85 L50 95 L60 85"
            stroke={ctx.c[t]}
            strokeWidth={3.2}
            fill="none"
            strokeLinejoin="round"
            data-slot={t}
          />
          <Both
            d="M10 94 Q18 89 27 89"
            stroke={ctx.c[t]}
            strokeWidth={2.6}
            fill="none"
            data-slot={t}
          />
        </g>
      );
    case 'hoodie':
      return (ctx) => (
        <g transform={low(ctx)}>
          <path d={SHOULDERS} {...main(ctx)} />
          <path d="M26 87 Q50 99 74 87 Q72 79 50 83 Q28 79 26 87 Z" {...main(ctx, 0.82)} />
          <path
            d="M45 91 L44 100 M55 91 L56 100"
            stroke={ctx.c[t]}
            strokeWidth={1.8}
            strokeLinecap="round"
            data-slot={t}
          />
        </g>
      );
    case 'turtleneck':
      return (ctx) => (
        <g transform={low(ctx)}>
          <path d={SHOULDERS} {...main(ctx)} />
          <rect x={37} y={78} width={26} height={14} rx={5} {...main(ctx, 0.9)} />
          <path
            d="M41 80 L41 91 M46 80 L46 91 M50 80 L50 91 M54 80 L54 91 M59 80 L59 91"
            stroke={darken(ctx.c[m], 0.72)}
            strokeWidth={0.8}
            {...THROUGH}
          />
        </g>
      );
    // A bib and straps in the neck's color over a shirt in its trim.
    case 'overalls':
      return (ctx) => (
        <g transform={low(ctx)}>
          {skinNeck(ctx)}
          <path d={SHOULDERS} {...trim(ctx)} />
          <path d="M35 92 L65 92 L66 112 L34 112 Z" {...main(ctx)} />
          <Both
            d="M37 93 L27 86.5"
            stroke={ctx.c[m]}
            strokeWidth={4.5}
            strokeLinecap="round"
            data-slot={m}
          />
          <g fill="#f2c230" stroke="#b8901c" strokeWidth={0.5} {...THROUGH}>
            <circle cx={38.5} cy={94.5} r={1.6} />
            <circle cx={61.5} cy={94.5} r={1.6} />
          </g>
        </g>
      );
    // A holiday sweater: a ribbed collar and a zigzag across the chest.
    case 'sweater':
      return (ctx) => (
        <g transform={low(ctx)}>
          {skinNeck(ctx)}
          <path d={SHOULDERS} {...main(ctx)} />
          <path d="M37 85 Q50 93 63 85 L63 89.5 Q50 98 37 89.5 Z" {...main(ctx, 0.82)} />
          <path
            d="M8 101 L13 96 L18 101 L23 96 L28 101 L33 96 L38 101 L43 96 L48 101 L53 96 L58 101 L63 96 L68 101 L73 96 L78 101 L83 96 L88 101 L93 96"
            stroke={ctx.c[t]}
            strokeWidth={2}
            fill="none"
            strokeLinejoin="round"
            data-slot={t}
          />
          <g {...trim(ctx)}>
            {[16, 26, 74, 84].map((x) => (
              <circle key={x} cx={x} cy={91} r={1.5} />
            ))}
          </g>
        </g>
      );
  }
}

const NECK: Record<Face['neck'], NeckDrawing> = {
  none: {},
  collar: { back: garment('collar', 'neck', 'neckTrim') },
  jersey: { back: garment('jersey', 'neck', 'neckTrim') },
  hoodie: { back: garment('hoodie', 'neck', 'neckTrim') },
  turtleneck: { back: garment('turtleneck', 'neck', 'neckTrim') },
  overalls: { back: garment('overalls', 'neck', 'neckTrim') },
  sweater: { back: garment('sweater', 'neck', 'neckTrim') },
  tie: {
    front: (ctx) => (
      <g transform={low(ctx)}>
        <path d="M36 88 L50 94 L64 88 L66 100 L34 100 Z" {...neckTrim(ctx)} />
        <path d="M46 92 L54 92 L52 96 L48 96 Z" {...neck(ctx)} />
        <path d="M48 96 L52 96 L55 104 L45 104 Z" {...neck(ctx)} />
      </g>
    ),
  },
  bowtie: {
    front: (ctx) => (
      <g transform={low(ctx)} {...neck(ctx)}>
        <path d="M39 89 L50 94 L39 99 Z" />
        <path d="M61 89 L50 94 L61 99 Z" />
        <circle cx={50} cy={94} r={2.6} fill={darken(ctx.c.neck, 0.8)} />
      </g>
    ),
  },
  scarf: {
    front: (ctx) => (
      <g transform={low(ctx)}>
        <path d="M60 89 L65 104 L74 102 L68 87 Z" {...neck(ctx, 0.88)} />
        <path d="M27 84 Q50 95 73 84 L75 92 Q50 104 25 92 Z" {...neck(ctx)} />
        <path
          d="M33 88.5 Q50 98 67 88.5"
          stroke={ctx.c.neckTrim}
          strokeWidth={2.2}
          fill="none"
          data-slot="neckTrim"
        />
      </g>
    ),
  },
  neckerchief: {
    front: (ctx) => (
      <g transform={low(ctx)}>
        <path d="M31 86 Q50 92 69 86 L50 102 Z" {...neck(ctx)} />
        <circle cx={50} cy={92} r={2.6} {...neck(ctx, 0.75)} />
      </g>
    ),
  },
  pearls: {
    front: (ctx) => (
      <g transform={low(ctx)} {...neck(ctx)} stroke={darken(ctx.c.neck, 0.8)} strokeWidth={0.8}>
        {[30, 36, 42, 50, 58, 64, 70].map((x) => (
          <circle key={x} cx={x} cy={90 + Math.abs(50 - x) * -0.12 + 2} r={3.2} />
        ))}
      </g>
    ),
  },
  medal: {
    front: (ctx) => (
      <g transform={low(ctx)}>
        <path
          d="M40 84 L48 93 M60 84 L52 93"
          stroke={ctx.c.neck}
          strokeWidth={4}
          data-slot="neck"
        />
        <circle
          cx={50}
          cy={94}
          r={5.5}
          {...neckTrim(ctx)}
          stroke={darken(ctx.c.neckTrim, 0.7)}
          strokeWidth={1}
        />
        <path d={star(50, 94, 2.8, 1.2)} fill={darken(ctx.c.neckTrim, 0.75)} {...THROUGH} />
      </g>
    ),
  },
  lanyard: {
    front: (ctx) => (
      <g transform={low(ctx)}>
        <path
          d="M39 86 L46 98 M61 86 L54 98"
          stroke={ctx.c.neck}
          strokeWidth={3}
          data-slot="neck"
        />
        <rect
          x={43}
          y={93}
          width={14}
          height={9}
          rx={1.5}
          {...neckTrim(ctx)}
          stroke={ctx.c.neck}
          strokeWidth={1.2}
        />
      </g>
    ),
  },
  whistle: {
    front: (ctx) => (
      <g transform={low(ctx)}>
        <path d="M44 86 L60 90" stroke={ctx.c.neck} strokeWidth={2} data-slot="neck" />
        <rect
          x={58}
          y={86}
          width={13}
          height={8}
          rx={3.5}
          {...neckTrim(ctx)}
          stroke={darken(ctx.c.neckTrim, 0.6)}
          strokeWidth={1.2}
        />
      </g>
    ),
  },
  chain: {
    front: (ctx) => (
      <g transform={low(ctx)}>
        <path
          d="M31 83 Q50 97 69 83"
          stroke={ctx.c.neck}
          strokeWidth={2.2}
          strokeDasharray="2.4 0.8"
          fill="none"
          data-slot="neck"
        />
        <circle
          cx={50}
          cy={92.5}
          r={3.2}
          {...neck(ctx)}
          stroke={darken(ctx.c.neck, 0.7)}
          strokeWidth={0.8}
        />
      </g>
    ),
  },

  // ── A season's ──
  cape: {
    // A vampire's: a high collar standing behind the head, lined.
    back: (ctx) => (
      <g transform={low(ctx)}>
        <Both d="M34 88 L8 50 Q10 74 18 90 Z" {...neck(ctx)} />
        <Both d="M31 86 L13 59 Q14 75 20 87 Z" {...neckTrim(ctx)} />
        {skinNeck(ctx)}
        <path d={SHOULDERS} {...neck(ctx)} />
        <path d="M41 85 L50 99 L59 85 Z" {...neckTrim(ctx)} />
        <circle
          cx={50}
          cy={88}
          r={2.4}
          fill="#f2c230"
          stroke="#b8901c"
          strokeWidth={0.6}
          {...THROUGH}
        />
      </g>
    ),
  },
};

// ── An outfit's shoulders on a badge ───────────────────────────────────────

const shirt = (ctx: Ctx, shade?: number) => paint(ctx.c, 'shirt', shade);
const shirtTrim = (ctx: Ctx, shade?: number) => paint(ctx.c, 'shirtTrim', shade);
/** A crew neck's ribbing, in the trim. */
const crew = (ctx: Ctx) => (
  <path
    d="M40 85.5 Q50 93 60 85.5"
    stroke={ctx.c.shirtTrim}
    strokeWidth={2.4}
    fill="none"
    data-slot="shirtTrim"
  />
);
/** Plain shoulders in the outfit, the neck between. */
const plain = (ctx: Ctx) => (
  <>
    {skinNeck(ctx)}
    <path d={SHOULDERS} {...shirt(ctx)} />
  </>
);

/**
 * What a badge shows of a chosen outfit (§2.1): its shoulders, under the chin,
 * in the outfit's own areas so the paint can fills them. The six that were
 * worn at the neck are the same drawings as there.
 */
const OUTFIT_BADGE: Record<FaceOutfit, (ctx: Ctx) => ReactNode> = {
  collar: garment('collar', 'shirt', 'shirtTrim'),
  jersey: garment('jersey', 'shirt', 'shirtTrim'),
  hoodie: garment('hoodie', 'shirt', 'shirtTrim'),
  turtleneck: garment('turtleneck', 'shirt', 'shirtTrim'),
  overalls: garment('overalls', 'shirt', 'shirtTrim'),
  sweater: garment('sweater', 'shirt', 'shirtTrim'),
  tee: (ctx) => (
    <g transform={low(ctx)}>
      {plain(ctx)}
      {crew(ctx)}
    </g>
  ),
  'heart-tee': (ctx) => (
    <g transform={low(ctx)}>
      {plain(ctx)}
      {crew(ctx)}
      <path d={heart(50, 101, 12)} {...shirtTrim(ctx)} />
    </g>
  ),
  tank: (ctx) => (
    <g transform={low(ctx)}>
      {skinNeck(ctx)}
      <path d={SHOULDERS} {...paint(ctx.c, 'skin', 0.94)} />
      <path d="M31 112 L33 93 Q41 99 50 99 Q59 99 67 93 L69 112 Z" {...shirt(ctx)} />
      <path
        d="M33 93 Q41 99 50 99 Q59 99 67 93"
        stroke={ctx.c.shirtTrim}
        strokeWidth={2}
        fill="none"
        data-slot="shirtTrim"
      />
    </g>
  ),
  dress: (ctx) => (
    <g transform={low(ctx)}>
      {skinNeck(ctx)}
      <path d={SHOULDERS} {...paint(ctx.c, 'skin', 0.94)} />
      <path d="M28 112 L31 96 Q50 103 69 96 L72 112 Z" {...shirt(ctx)} />
      <Both d="M33 97 L36 86" stroke={ctx.c.shirt} strokeWidth={2.2} data-slot="shirt" />
      <path
        d="M31 96 Q50 103 69 96"
        stroke={ctx.c.shirtTrim}
        strokeWidth={1.8}
        fill="none"
        data-slot="shirtTrim"
      />
    </g>
  ),
  suit: (ctx) => (
    <g transform={low(ctx)}>
      {plain(ctx)}
      <path d="M40 85 L50 104 L60 85 Z" {...shirtTrim(ctx)} />
      <Both d="M40 85 L50 104 L43 112 L29 90 Z" {...shirt(ctx, 0.85)} />
    </g>
  ),
  referee: (ctx) => (
    <g transform={low(ctx)}>
      {plain(ctx)}
      <clipPath id={`${ctx.uid}-ref`}>
        <path d={SHOULDERS} />
      </clipPath>
      <g clipPath={`url(#${ctx.uid}-ref)`} {...shirtTrim(ctx)}>
        {[8, 20, 32, 44, 56, 68, 80, 92].map((x) => (
          <rect key={x} x={x - 2.5} y={84} width={5} height={30} />
        ))}
      </g>
      {crew(ctx)}
    </g>
  ),
  coach: (ctx) => (
    <g transform={low(ctx)}>
      {plain(ctx)}
      <Both d="M40 84 L50 93 L44 98 L36 89 Z" {...shirt(ctx, 0.85)} />
      <path d="M50 93 L50 112" stroke={ctx.c.shirtTrim} strokeWidth={1.6} data-slot="shirtTrim" />
    </g>
  ),
  puffer: (ctx) => (
    <g transform={low(ctx)}>
      {plain(ctx)}
      <path
        d="M6 100 Q50 106 94 100 M3 108 Q50 114 97 108"
        stroke={darken(ctx.c.shirt, 0.8)}
        strokeWidth={1.2}
        fill="none"
        {...THROUGH}
      />
      <rect x={37} y={80} width={26} height={12} rx={5} {...shirt(ctx, 0.92)} />
      <path d="M50 81 L50 112" stroke={ctx.c.shirtTrim} strokeWidth={1.4} data-slot="shirtTrim" />
    </g>
  ),
  tracksuit: (ctx) => (
    <g transform={low(ctx)}>
      {plain(ctx)}
      <Both
        d="M8 112 L12 94 Q16 89 26 87"
        stroke={ctx.c.shirtTrim}
        strokeWidth={2.4}
        fill="none"
        data-slot="shirtTrim"
      />
      <rect x={40} y={81} width={20} height={9} rx={3} {...shirt(ctx, 0.9)} />
      <path d="M50 82 L50 112" stroke={ctx.c.shirtTrim} strokeWidth={1.4} data-slot="shirtTrim" />
    </g>
  ),
  super: (ctx) => (
    <g transform={low(ctx)}>
      {plain(ctx)}
      {crew(ctx)}
      <path d={star(50, 104, 7, 3)} {...shirtTrim(ctx)} />
    </g>
  ),
  robot: (ctx) => (
    <g transform={low(ctx)}>
      <rect x={42} y={76} width={16} height={16} {...paint(ctx.c, 'shirt', 0.8)} />
      <path d={SHOULDERS} {...shirt(ctx)} stroke={darken(ctx.c.shirt, 0.62)} strokeWidth={1.4} />
      <rect x={38} y={96} width={24} height={16} rx={2} {...shirt(ctx, 0.88)} />
      <circle cx={50} cy={103} r={3} {...shirtTrim(ctx)} />
    </g>
  ),
  santa: (ctx) => (
    <g transform={low(ctx)}>
      {plain(ctx)}
      <path d="M36 84 Q50 95 64 84 L66 90 Q50 101 34 90 Z" {...shirtTrim(ctx)} />
      <path d="M50 97 L50 112" stroke={ctx.c.shirtTrim} strokeWidth={5} data-slot="shirtTrim" />
    </g>
  ),
  'pumpkin-suit': (ctx) => (
    <g transform={low(ctx)}>
      {plain(ctx)}
      <path
        d="M30 112 Q28 96 34 90 M70 112 Q72 96 66 90 M50 96 L50 112"
        stroke={darken(ctx.c.shirt, 0.8)}
        strokeWidth={1.4}
        fill="none"
        {...THROUGH}
      />
      <path d="M38 85 Q44 92 50 87 Q56 92 62 85 Q56 95 50 91 Q44 95 38 85 Z" {...shirtTrim(ctx)} />
    </g>
  ),
  flannel: (ctx) => (
    <g transform={low(ctx)}>
      {plain(ctx)}
      <clipPath id={`${ctx.uid}-plaid`}>
        <path d={SHOULDERS} />
      </clipPath>
      <g clipPath={`url(#${ctx.uid}-plaid)`} stroke={ctx.c.shirtTrim} data-slot="shirtTrim">
        {[12, 30, 70, 88].map((x) => (
          <path key={x} d={`M${x} 84 L${x} 114`} strokeWidth={3} />
        ))}
        <path d="M0 98 L100 98 M0 108 L100 108" strokeWidth={2.4} />
      </g>
      <Both d="M40 84 L50 93 L42 97 Z" {...shirt(ctx, 0.85)} />
    </g>
  ),
  leprechaun: (ctx) => (
    <g transform={low(ctx)}>
      {skinNeck(ctx)}
      <path d={SHOULDERS} fill="#f4f4f4" {...THROUGH} />
      <Both d="M4 112 L6 100 Q8 89 26 87 L40 86 L48 112 Z" {...shirt(ctx)} />
      <g {...shirtTrim(ctx)}>
        <circle cx={44} cy={101} r={1.8} />
        <circle cx={56} cy={101} r={1.8} />
      </g>
    </g>
  ),
};

/**
 * Where a part is in the box, for a picker that shows it large: the maker's
 * choices are drawn on your face and cropped here where the part is small.
 */
export const FACE_PART_VIEW: Partial<Record<string, string>> = {
  eyes: '22 38 56 34',
  brows: '22 30 56 34',
  nose: '30 44 40 34',
  mouth: '28 54 44 34',
  facial: '12 44 76 56',
  eyewear: '8 30 84 48',
  neck: '4 62 92 38',
  ears: '0 26 100 60',
};
